import UserManagement from "./UserManagement";
import RigDetail from "./RigDetail";
import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../components/LanguageToggle";
import expiryLabel from "../utils/expiryLabel";
import { canManageAfes, afeStatusColor } from "../utils/afe";
import { weatherLang, riskMessage } from "../utils/weather";
import { jobTypeLabel } from "../utils/jsaLabels";
import FeedbackButton from "../components/FeedbackButton";
import HelpButton from "../components/HelpButton";
import SmsSettingsButton from "../components/SmsSettingsButton";
import NewReport from "./NewReport";
import NewWell from "./NewWell";
import NewExpense from "./NewExpense";
import WellDetail from "./WellDetail";
import ChatPanel from "../components/ChatPanel";
import { normalizeWells } from "../utils/wellHelpers";
import ErrorBanner from "../components/ErrorBanner";
import { LoadingLine, PageFallback } from "../components/Spinner";
import Pagination from "../components/Pagination";
import ServiceDashboardPanel from "../components/ServiceDashboardPanel";
import { todayKey } from "../components/fieldUi";
import { formatDate, formatDateUTC } from "../utils/dates";
import { wellPhaseLabel } from "../utils/wellPhase";
import { showNoJsaWarning } from "../utils/jsaWarning";
import { canWriteExpenses } from "../utils/roles";
import { isServiceCompany, tabHiddenForOrg, moduleGroupFor, hasModule, hasBothModules, isServiceOnly, simpleMenu } from "../utils/orgType";
import AccountMenu from "../components/AccountMenu";
import RoleHome, { homeOf } from "../components/RoleHome";

const ProcedureAnalyzer = lazy(() => import("./ProcedureAnalyzer"));
const TallySheet = lazy(() => import("./TallySheet"));
const PipeTally = lazy(() => import("./PipeTally"));
const GISMap = lazy(() => import("./GISMap"));
const SafetyDashboard = lazy(() => import("./SafetyDashboard"));
const VendorDashboard = lazy(() => import("./VendorDashboard"));
const EquipmentDashboard = lazy(() => import("./EquipmentDashboard"));
const EquipmentDetail = lazy(() => import("./EquipmentDetail"));
const AuditLog = lazy(() => import("./AuditLog"));
const AnalyticsDashboard = lazy(() => import("./AnalyticsDashboard"));
const BHAPerformanceAnalysis = lazy(() => import("./BHAPerformanceAnalysis"));
const PumperRoute = lazy(() => import("./PumperRoute"));
const LeaseOperatingDashboard = lazy(() => import("./LeaseOperatingDashboard"));
const PumperRouteAdmin = lazy(() => import("./PumperRouteAdmin"));
const FeedbackAdmin = lazy(() => import("./FeedbackAdmin"));
const AccessManagement = lazy(() => import("./AccessManagement"));
const LegalCenter = lazy(() => import("./LegalCenter"));
const WellBulkImport = lazy(() => import("./WellBulkImport"));
const TicketScanner = lazy(() => import("./TicketScanner"));
const LeaseManagement = lazy(() => import("./LeaseManagement"));
const AccountingOverview = lazy(() => import("./AccountingOverview"));
const DuplicateReview = lazy(() => import("./DuplicateReview"));
const FleetDashboard = lazy(() => import("./FleetDashboard"));
const OperationsCalendar = lazy(() => import("./OperationsCalendar"));
const PersonnelCertifications = lazy(() => import("./PersonnelCertifications"));
const RigReports = lazy(() => import("./RigReports"));
const Jobs = lazy(() => import("./Jobs"));
const Customers = lazy(() => import("./Customers"));
const HydroTickets = lazy(() => import("./HydroTickets"));
const DivisionPnl = lazy(() => import("./DivisionPnl"));
const QuickBooksExport = lazy(() => import("./QuickBooksExport"));
const Billing = lazy(() => import("./Billing"));
const SettingsScreen = lazy(() => import("./Settings"));

const TAB_PERMISSIONS = {
  SAFETY: ["admin", "supervisor", "safety_officer", "field_hand"],
  TALLY: ["admin", "supervisor", "field_hand"],
  VENDOR: ["admin", "supervisor"],
  MAP: ["admin", "supervisor", "safety_officer", "field_hand", "consultant"],
  COMMS: ["admin", "supervisor", "safety_officer", "field_hand"],
  USERS: ["admin"],
  ANALYZE: ["admin", "supervisor", "consultant"],
  EQUIPMENT: ["admin", "supervisor", "safety_officer", "consultant"],
  AUDIT_LOG: ["admin", "supervisor"],
  // Matches the backend's canView on /api/analytics/summary — same tier as
  // ANALYZE rather than the wide-open wells/expenses view, since this
  // surfaces aggregate spend/cost-per-barrel rather than a single record.
  ANALYTICS: ["admin", "supervisor", "consultant"],
  // Matches the backend's role gate on GET /api/wells/bha-performance
  // exactly (routes/wells.js) — same tier as ANALYTICS, chosen there for
  // the same reason: cross-well aggregate data, not a single record.
  BHA_PERFORMANCE: ["admin", "supervisor", "consultant"],
  // "My Route" (pages/PumperRoute.js) — same write tier as TALLY:
  // field_hand is the pumper submitting daily well checks, admin/supervisor
  // can audit/fill in on any route.
  PUMPER_ROUTE: ["admin", "supervisor", "field_hand"],
  // Financial data — matches the backend's canViewFinancials gate on
  // /api/lease-operating-statements (admin/supervisor only, no field_hand).
  LEASE_OPERATING: ["admin", "supervisor"],
  // "Route Setup" (pages/PumperRouteAdmin.js) — assigning routes/pumpers
  // is a supervisory task; matches the backend's canManage gate on
  // /api/pumper-routes create/update/delete (and reads of other pumpers'
  // routes).
  PUMPER_ROUTE_ADMIN: ["admin", "supervisor"],
  // Matches GET/PUT /api/feedback exactly — admin only, not supervisor,
  // per the existing backend gate (not something being loosened here).
  FEEDBACK_ADMIN: ["admin"],
  // Matches the backend's demoAdminOnly gate on /api/demo-access exactly —
  // admin only. Also requires the user's own organization to be "RigIQ
  // Demo Co" (see the isDemoOrg check on visibleNavButtons below); role
  // alone isn't enough, since this tool has no business being visible to
  // any customer org's admin.
  ACCESS_MANAGEMENT: ["admin"],
  // Matches the backend's canView on /api/leases (admin/supervisor only) —
  // legal/financial lease terms, same tier as LEASE_OPERATING and VENDOR.
  LEASE: ["admin", "supervisor"],
  // Standalone AR/AP + interest-distribution system-of-record — same
  // admin/supervisor tier as LEASE/LEASE_OPERATING, matching the backend's
  // canView on /api/vendor-bills, /api/revenue-invoices,
  // /api/interest-owners, /api/interest-distributions, and /api/accounting.
  ACCOUNTING: ["admin", "supervisor"],
  // Duplicate review (one cost record migration): admins decide, supervisors
  // flag — matches routes/costReview.js. Shown only when the org has items.
  COST_REVIEW: ["admin", "supervisor"],
  // Fleet dashboard + leaderboard — matches utils/fleetScope.js FLEET_ROLES.
  FLEET: ["admin", "supervisor"],
  // Aggregates maintenance/compliance/lease/AP/AR into one view, so it has
  // to match the most restrictive of those source tiers (LEASE/ACCOUNTING
  // are admin/supervisor only, tighter than EQUIPMENT's) — matches the
  // backend's canView on /api/calendar/events exactly.
  CALENDAR: ["admin", "supervisor"],
  // Open to every role — the nav button always leads to
  // PersonnelCertifications.js, but that page (and the backend behind it)
  // self-limits to "your own records only" for anyone who isn't
  // admin/supervisor. See personnelCertificationController.js's "own data
  // vs. everyone's data" split.
  CERTIFICATIONS: ["admin", "supervisor", "field_hand", "safety_officer", "consultant"],
  // Open to every role, same as CERTIFICATIONS above — every user can see
  // what they've personally accepted and re-download their own
  // certificate, and the reference documents (Security/Data Ownership/SLA)
  // aren't restricted material. No backend gate to match here since
  // /api/legal/certificates and /api/legal/ai-terms-status already scope
  // to req.user.id — there's nothing this nav check is protecting beyond
  // discoverability.
  LEGAL_CENTER: ["admin", "supervisor", "field_hand", "safety_officer", "consultant"],
  // Service companies only (see SERVICE_ONLY_TABS). Rig reports and jobs
  // are readable by everyone in the org (the backend gates writes);
  // customers are office work — matches /api/customers writes.
  RIG_REPORTS: ["admin", "supervisor", "field_hand", "safety_officer", "consultant"],
  // The full rig cards (rig services keep them off the dashboard).
  RIGS: ["admin", "supervisor", "field_hand", "safety_officer", "consultant"],
  JOBS: ["admin", "supervisor", "field_hand", "safety_officer", "consultant"],
  CUSTOMERS: ["admin", "supervisor"],
  // Hydro-testing module (docs/features/DIVISION-PL.md): tickets like rig
  // reports; the Division P&L is money — admin / supervisor, matching
  // /api/division-pnl.
  HYDRO_TICKETS: ["admin", "supervisor", "field_hand", "safety_officer", "consultant"],
  DIVISION_PNL: ["admin", "supervisor"],
  // QuickBooks CSV export — office work, matches /api/quickbooks.
  QUICKBOOKS: ["admin", "supervisor"],
  // Billing (workflow stage 4) — office work, matches /api/billing.
  BILLING: ["admin", "supervisor"],
  // Settings in one place (workflow stage 5) — the admin's.
  SETTINGS: ["admin"],
};

// Which tabs show depends on the org's modules (utils/orgType.js
// tabHiddenForOrg): RIG_REPORTS / JOBS / CUSTOMERS need rigServices,
// LEASE / LEASE_OPERATING need production, the pumper tabs need pumper.

// Desktop nav grouping only — a display concern layered on top of the tabs
// above, not a second permissions system. Which roles see which tab is
// still decided exclusively by TAB_PERMISSIONS; a group here just controls
// which dropdown a tab's button renders inside once it's already passed
// that check. Grouped by what each page is actually for: OPERATIONS is
// day-to-day field work (safety events, tally sheets, vendor tickets, the
// well map), INTELLIGENCE is look-back/analysis tooling (AI procedure
// analysis, cost analytics, the audit trail), ADMIN is fleet/user
// management (equipment, user accounts).
const NAV_GROUP_ORDER = ["OPERATIONS", "INTELLIGENCE", "ADMIN"];
// Values are i18next keys, resolved with t() where NAV_GROUP_LABELS is
// consumed inside the component (module scope has no hook access).
// Short menus (workflow stage 6) for a rig-service / hydro company with
// the trimmed menu: each role's items first (≤ 10 with "More"); every other
// item it can open is under More.
const SHORT_MENUS = {
  admin: ["RIG_REPORTS", "HYDRO_TICKETS", "JOBS", "CUSTOMERS", "RIGS", "BILLING", "DIVISION_PNL", "FLEET", "SETTINGS"],
  office: ["BILLING", "RIG_REPORTS", "HYDRO_TICKETS", "CUSTOMERS", "JOBS", "RIGS", "SAFETY", "CALENDAR", "CERTIFICATIONS"],
  manager: ["RIG_REPORTS", "HYDRO_TICKETS", "RIGS", "JOBS", "CUSTOMERS", "BILLING", "SAFETY", "CALENDAR", "CERTIFICATIONS"],
  toolpusher: ["RIG_REPORTS", "TALLY", "SAFETY", "CERTIFICATIONS", "COMMS"],
  crew: ["RIG_REPORTS", "SAFETY", "CERTIFICATIONS", "COMMS"],
  hydro: ["HYDRO_TICKETS", "SAFETY", "CERTIFICATIONS", "COMMS"],
  // a field hand not on the roster yet
  field: ["RIG_REPORTS", "HYDRO_TICKETS", "TALLY", "SAFETY", "CERTIFICATIONS", "COMMS"],
};
function shortMenuFor(user) {
  if (user.role === "admin") return SHORT_MENUS.admin;
  if (user.role === "supervisor") return user.crewPosition === "office" ? SHORT_MENUS.office : SHORT_MENUS.manager;
  if (user.role !== "field_hand") return null;
  if (!user.crewPosition) return SHORT_MENUS.field;
  return SHORT_MENUS[homeOf(user)] || SHORT_MENUS.field;
}

const NAV_GROUP_LABELS = {
  // Only used by orgs with both Production and Rig services
  // (utils/orgType.js moduleGroupFor).
  PRODUCTION: "dashboard.nav.groupProduction",
  RIG_SERVICES: "dashboard.nav.groupRigServices",
  OPERATIONS: "dashboard.nav.groupOperations",
  INTELLIGENCE: "dashboard.nav.groupIntelligence",
  ADMIN: "dashboard.nav.groupAdmin",
  MORE: "dashboard.nav.groupMore",
};

const EQUIPMENT_WRITE_ROLES = ["admin", "supervisor", "safety_officer"];
// Who may write a rig report (the server's canWrite), and add / import
// wells (the server's createWell / bulk import).
const WRITE_REPORT_ROLES = ["admin", "supervisor", "field_hand"];
const WELL_WRITE_ROLES = ["admin", "supervisor"];

const RISK_COLORS = {
  Low: "#00cc66",
  Medium: "#ffaa00",
  High: "#ff6600",
  Critical: "#ff4466",
};

const STORM_RING_COLORS = {
  CRITICAL: "#ff4466",
  DANGER: "#ff4466",
  WARNING: "#ff6600",
  WATCH: "#ffaa00",
  WIND: "#ffaa00",
};

// ─── RESPONSIVE HOOK ─────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function LightningBadge({ risk }) {
  const { t } = useTranslation();
  if (!risk) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        backgroundColor: risk.color + "22",
        border: `1px solid ${risk.color}`,
        borderRadius: "6px",
        padding: "6px 10px",
        marginTop: "10px",
      }}
    >
      <span style={{ fontSize: "16px" }}>⚡</span>
      <div>
        <div
          style={{
            color: risk.color,
            fontSize: "10px",
            fontWeight: "700",
            letterSpacing: "2px",
          }}
        >
          {t(`dashboard.main.lightning.${risk.level}`, { defaultValue: risk.level })} —{" "}
          {t("dashboard.main.miRadius", { miles: risk.zone })}
        </div>
        <div style={{ color: risk.color, fontSize: "10px", opacity: 0.8 }}>
          {riskMessage(t, risk)}
        </div>
      </div>
    </div>
  );
}

// Small notification-count pill for a nav button — reuses the same red
// already used everywhere else in the app for "needs attention" states
// (overdue maintenance, expired compliance, out-of-service equipment).
function NavBadge({ count }) {
  return (
    <span
      style={{
        display: "inline-block",
        marginLeft: "6px",
        backgroundColor: "#ff4466",
        color: "#fff",
        fontSize: "9px",
        fontWeight: "700",
        borderRadius: "10px",
        padding: "1px 6px",
        minWidth: "14px",
        textAlign: "center",
      }}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

// Desktop-only grouped nav dropdown — one of these renders per non-empty
// entry in `navGroups`. Reuses each tab's own onClick (already wired to
// its setShowX(true) call) so opening a tab from inside the dropdown
// behaves identically to the old flat button; this just adds closing the
// dropdown afterward.
function NavGroupDropdown({ group, isOpen, onToggle, onSelect, navBtnStyle }) {
  const border = "rgba(0,245,255,0.15)";
  const badgeTotal = group.items.reduce((sum, btn) => sum + (btn.badge || 0), 0);

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={onToggle}
        style={navBtnStyle(isOpen)}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = "rgba(0,245,255,0.12)";
            e.currentTarget.style.borderColor = "rgba(0,245,255,0.4)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = "rgba(0,245,255,0.05)";
            e.currentTarget.style.borderColor = "rgba(0,245,255,0.2)";
          }
        }}
      >
        {group.label}
        <span style={{ marginLeft: "6px", fontSize: "9px" }}>{isOpen ? "▲" : "▼"}</span>
        {badgeTotal > 0 && <NavBadge count={badgeTotal} />}
      </button>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            backgroundColor: "rgba(2,8,23,0.98)",
            border: `1px solid ${border}`,
            borderRadius: "8px",
            padding: "6px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            minWidth: "190px",
            zIndex: 50,
            boxShadow: "0 12px 28px rgba(0,0,0,0.5)",
          }}
        >
          {group.items.map((btn) => (
            <button
              key={btn.label}
              onClick={() => onSelect(btn)}
              style={{ ...navBtnStyle(false), width: "100%" }}
            >
              {btn.label}
              {btn.badge != null && <NavBadge count={btn.badge} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Dashboard({ user, token, onLogout, initialEquipmentId }) {
  const { t, i18n } = useTranslation();
  const [showProcedureAnalyzer, setShowProcedureAnalyzer] = useState(false);
  const isMobile = useIsMobile();
  // No letter-spacing on a phone so the full label fits at 360px in
  // Spanish too ("📱 Alertas SMS", "💬 Comentarios"); ellipsis only as a
  // last resort for an even narrower screen.
  const mobileToolbarBtn = {
    width: "100%",
    minHeight: "44px",
    padding: "0 4px",
    fontSize: "12px",
    letterSpacing: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
  const [showTally, setShowTally] = useState(false);
  // "Show all menu items" (admin, rig-service-only companies): saved on the
  // org; kept here too so the menu changes at once.
  const [menuShowAll, setMenuShowAll] = useState(!!user.organizationId?.menuShowAll);
  const menuUser = user.organizationId && typeof user.organizationId === "object" ? { ...user, organizationId: { ...user.organizationId, menuShowAll } } : user;
  const serviceOnly = isServiceOnly(user);
  const trimmedMenu = simpleMenu(menuUser);
  // The role home (stage 6): a toolpusher / crew sees only their rig; a
  // hydro operator only hydro; admin / office the dashboard as it is.
  const home = trimmedMenu ? homeOf(user) : "admin";
  const crewHome = home === "toolpusher" || home === "crew";
  const hydroHome = home === "hydro";
  async function toggleShowAll(on) {
    await axios.put(`${process.env.REACT_APP_API_URL}/api/organization/menu`, { showAll: on }, { headers: { Authorization: `Bearer ${token}` } });
    setMenuShowAll(on);
  }
  // Rig services: TALLY is the pipe tally; an org with production can still open its well tallies.
  const [wellTallies, setWellTallies] = useState(false);
  const [showVendor, setShowVendor] = useState(false);
  const [showEquipment, setShowEquipment] = useState(false);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(
    () => initialEquipmentId || null,
  );
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showBhaPerformance, setShowBhaPerformance] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const [showPumperRoute, setShowPumperRoute] = useState(false);
  const [showLeaseOperating, setShowLeaseOperating] = useState(false);
  const [showPumperRouteAdmin, setShowPumperRouteAdmin] = useState(false);
  const [showFeedbackAdmin, setShowFeedbackAdmin] = useState(false);
  const [showAccessManagement, setShowAccessManagement] = useState(false);
  const [showLegalCenter, setShowLegalCenter] = useState(false);
  const [showGIS, setShowGIS] = useState(false);
  const [selectedWell, setSelectedWell] = useState(null);
  // Set to "tanks" when PumperRoute's "Set up tanks" link opens WellDetail
  // directly on that tab — reset to "overview" whenever WellDetail closes
  // so a normal well-card click doesn't inherit a stale deep link.
  const [wellDetailInitialTab, setWellDetailInitialTab] = useState("overview");
  const [selectedRig, setSelectedRig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showWellForm, setShowWellForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showTicketScanner, setShowTicketScanner] = useState(false);
  const [showLeases, setShowLeases] = useState(false);
  const [showAccounting, setShowAccounting] = useState(false);
  const [showCostReview, setShowCostReview] = useState(false);
  const [showFleet, setShowFleet] = useState(false);
  // { open, total, migrated } — the menu entry appears only when the cost
  // migration left something to review in this org.
  const [costReviewCount, setCostReviewCount] = useState(null);
  useEffect(() => {
    if (user.role !== "admin" && user.role !== "supervisor") return undefined;
    let live = true;
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/cost-review/count`, { headers: { Authorization: "Bearer " + token } })
      .then((res) => live && setCostReviewCount(res.data))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [token, user.role, showCostReview]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showCertifications, setShowCertifications] = useState(false);
  // { status?, startNew? } while Rig Reports is open (service companies).
  const [rigReportsView, setRigReportsView] = useState(null);
  const [showJobs, setShowJobs] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [showHydro, setShowHydro] = useState(false);
  const [showPnl, setShowPnl] = useState(false);
  const [showQb, setShowQb] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  // Settings: "" closed, else the section to open ("open" = the list)
  const [settingsAt, setSettingsAt] = useState("");
  const serviceCompany = isServiceCompany(user);
  // Rig services without production: the compact dashboard (KPIs, needs
  // attention, rigs today). Their wells are customers' wells (Jobs /
  // Customers), so no well grid.
  const rigOnly = serviceCompany && !hasModule(user, "production");
  // GET /api/reports/service-dashboard — loaded with the dashboard (it also
  // says which wells have a rig working today: weather / JSA only there).
  const [serviceData, setServiceData] = useState(undefined);
  const [workingWellIds, setWorkingWellIds] = useState(null);
  const [showRigs, setShowRigs] = useState(false);
  // Equipment opened from "Rigs & equipment": back goes to the rigs.
  const [equipmentFromRigs, setEquipmentFromRigs] = useState(false);
  const hasProduction = hasModule(user, "production");
  const bothModules = hasBothModules(user);
  const [certDeepLinkUserId, setCertDeepLinkUserId] = useState(null);
  const [leaseDeepLinkId, setLeaseDeepLinkId] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showRigForm, setShowRigForm] = useState(false);
  const [showUserMgmt, setShowUserMgmt] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [openNavGroup, setOpenNavGroup] = useState(null);
  const [phoneMoreOpen, setPhoneMoreOpen] = useState(false);
  const navGroupRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [expenses, setExpenses] = useState([]);
  const [expensesTotal, setExpensesTotal] = useState(0);
  const [wells, setWells] = useState([]);
  const [wellsPage, setWellsPage] = useState(1);
  const [wellsTotal, setWellsTotal] = useState(0);
  const [wellsTotalPages, setWellsTotalPages] = useState(1);
  const [wellSearchInput, setWellSearchInput] = useState("");
  const [wellSearch, setWellSearch] = useState("");
  const [wellBasinInput, setWellBasinInput] = useState("");
  const [wellBasin, setWellBasin] = useState("");
  const [wellLocationInput, setWellLocationInput] = useState("");
  const [wellLocation, setWellLocation] = useState("");
  const [wellPhaseFilter, setWellPhaseFilter] = useState("");
  const [wellStatusFilter, setWellStatusFilter] = useState("");
  const [wellSortBy, setWellSortBy] = useState("createdAt");
  const [wellSortOrder, setWellSortOrder] = useState("desc");
  const [rigs, setRigs] = useState([]);
  const [weather, setWeather] = useState({});
  const [weatherErrors, setWeatherErrors] = useState({});
  const [jsaMap, setJsaMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [globalAlert, setGlobalAlert] = useState(null);
  const [rigForm, setRigForm] = useState({
    name: "",
    type: "Workover Unit",
    contractor: "",
  });
  const [rigError, setRigError] = useState("");

  const headers = { Authorization: "Bearer " + token };

  // Deep link consumed into selectedEquipmentId's initial state above —
  // clear it from the URL bar so a later refresh lands on the normal
  // dashboard instead of re-entering the same equipment every time.
  useEffect(() => {
    if (initialEquipmentId) {
      window.history.replaceState(null, "", "/");
    }
    // eslint-disable-next-line
  }, []);

  // Debounce the free-text well filters so typing doesn't fire a request
  // per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setWellSearch(wellSearchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [wellSearchInput]);
  useEffect(() => {
    const t = setTimeout(() => setWellBasin(wellBasinInput.trim()), 400);
    return () => clearTimeout(t);
  }, [wellBasinInput]);
  useEffect(() => {
    const t = setTimeout(() => setWellLocation(wellLocationInput.trim()), 400);
    return () => clearTimeout(t);
  }, [wellLocationInput]);

  // Everything the dashboard waits on at startup gives up after 20 s, and
  // one request failing or timing out never keeps "LOADING SYSTEMS…" up
  // (the row only shows while something is loading).
  const fetchData = useCallback(async (page = 1) => {
    const headers = { Authorization: "Bearer " + token };
    const timeout = 20000;
    try {
      const wellParams = { page, limit: 25, sortBy: wellSortBy, sortOrder: wellSortOrder };
      if (wellSearch) wellParams.search = wellSearch;
      if (wellBasin) wellParams.basin = wellBasin;
      if (wellLocation) wellParams.location = wellLocation;
      if (wellPhaseFilter) wellParams.phase = wellPhaseFilter;
      if (wellStatusFilter) wellParams.status = wellStatusFilter;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/wells`,
        { headers, params: wellParams, timeout },
      );
      const normalized = normalizeWells(res.data.data);
      setWells(normalized);
      setWellsPage(res.data.page);
      setWellsTotal(res.data.total);
      setWellsTotalPages(res.data.totalPages);

      const weatherResults = {};
      const weatherFailures = {};
      const jsaResults = {};
      let highestAlert = null;

      // Rig services: weather and "no JSA today" only for wells with a rig
      // working today (one cached call per location) — never for a list of
      // customer wells with no crew on location.
      let working = null;
      if (serviceCompany && hydroHome) {
        working = new Set(); // a hydro operator's home is hydro only: no rig dashboard
        setWorkingWellIds(working);
      } else if (serviceCompany) {
        try {
          const sd = await axios.get(`${process.env.REACT_APP_API_URL}/api/reports/service-dashboard`, { headers, params: { today: todayKey(), lang: weatherLang(i18n), ...(crewHome ? { scope: "mine" } : {}) }, timeout });
          setServiceData(sd.data || {});
          working = new Set((sd.data?.rigs || []).filter((r) => r.state === "working" && r.currentWell?.wellId).map((r) => String(r.currentWell.wellId)));
        } catch {
          setServiceData(undefined); // the panel loads it itself and shows the error
          working = new Set();
        }
        setWorkingWellIds(working);
      }
      const forConditions = rigOnly ? [] : working ? normalized.filter((w) => working.has(String(w.id || w._id))) : normalized;

      // Weather and JSA come in the background (each call gives up after
      // 10 s): the dashboard never waits on them — a slow or hung weather
      // call used to leave "LOADING SYSTEMS…" up for good.
      const conditions = Promise.all(
        forConditions.map(async (well) => {
          const wellId = well.id || well._id;
          try {
            const w = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/weather`,
              { headers, params: { lat: well.latitude, lon: well.longitude, lang: weatherLang(i18n) }, timeout: 10000 },
            );
            weatherResults[wellId] = w.data;
            if (w.data.lightningRisk) {
              const levels = ["WATCH", "WIND", "WARNING", "DANGER", "CRITICAL"];
              if (
                !highestAlert ||
                levels.indexOf(w.data.lightningRisk.level) >
                  levels.indexOf(highestAlert.level)
              ) {
                highestAlert = {
                  ...w.data.lightningRisk,
                  wellName: well.well_name,
                };
              }
            }
          } catch (err) {
            weatherFailures[wellId] =
              err.response?.data?.error || t("dashboard.main.weatherUnavailableShort");
          }
          try {
            const jsa = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/jsa/${wellId}/today`,
              { headers, timeout: 10000 },
            );
            if (jsa.data) jsaResults[wellId] = jsa.data;
          } catch {}
        }),
      )
        .then(() => {
          setWeather(weatherResults);
          setWeatherErrors(weatherFailures);
          setJsaMap(jsaResults);
          setGlobalAlert(highestAlert);
        })
        .catch(() => {});
      void conditions;

      // (each on its own: one a role can't open, or that times out, doesn't stop the others)
      const [reportsRes, expensesRes, rigsRes] = await Promise.all(
        ["/api/reports", "/api/expenses", "/api/rigs"].map((p) => axios.get(`${process.env.REACT_APP_API_URL}${p}`, { headers, timeout }).catch(() => null)),
      );
      if (reportsRes) {
        setReports(reportsRes.data.data);
        setReportsTotal(reportsRes.data.total);
      }
      if (expensesRes) {
        setExpenses(expensesRes.data.data);
        setExpensesTotal(expensesRes.data.total);
      }
      if (rigsRes) setRigs(rigsRes.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("dashboard.main.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [token, wellSearch, wellBasin, wellLocation, wellPhaseFilter, wellStatusFilter, wellSortBy, wellSortOrder, t, i18n, serviceCompany, rigOnly, crewHome, hydroHome]);

  function handleWellsPageChange(newPage) {
    setLoading(true);
    fetchData(newPage);
  }

  async function deleteWell(wellId, e) {
    e.stopPropagation();
    if (!wellId || wellId === "undefined") return;
    if (!window.confirm(t("dashboard.main.confirmDeleteWell"))) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/wells/${wellId}`,
        { headers },
      );
      fetchData();
    } catch (err) {
      console.error("Failed to delete well", err.response?.data);
    }
  }

  async function createRig() {
    // A service company's rigs are its own — the backend fills in the org as contractor.
    if (!rigForm.name || (!rigForm.contractor && !serviceCompany)) {
      setRigError(t("dashboard.main.rigRequired"));
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/rigs`, rigForm, {
        headers: { ...headers, "Content-Type": "application/json" },
      });
      setShowRigForm(false);
      setRigForm({ name: "", type: "Workover Unit", contractor: "" });
      setRigError("");
      fetchData();
    } catch {
      setRigError(t("dashboard.main.rigCreateFailed"));
    }
  }

  async function deleteRig(rigId, e) {
    e.stopPropagation();
    if (!window.confirm(t("dashboard.main.confirmDeleteRig"))) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/rigs/${rigId}`, {
        headers,
      });
      fetchData();
    } catch {
      console.error("Failed to delete rig");
    }
  }

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Equipment alert badge — only fetched for roles that can actually see
  // the Equipment tab (same set the backend's canView already enforces on
  // GET /api/equipment/alerts-digest), so other roles don't take a 403 on
  // every load. Re-fetched whenever the equipment view closes, so acting
  // on an alert (completing maintenance, renewing a cert) updates the
  // badge without needing a full page reload.
  const [equipmentAlertCount, setEquipmentAlertCount] = useState(0);
  const canViewEquipment = TAB_PERMISSIONS.EQUIPMENT.includes(user.role);

  const fetchEquipmentAlertCount = useCallback(async () => {
    if (!canViewEquipment) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/equipment/alerts-digest`,
        { headers: { Authorization: "Bearer " + token } },
      );
      setEquipmentAlertCount(res.data.badgeCount);
    } catch {
      // Non-critical — the badge just stays at its last known value.
    }
    // eslint-disable-next-line
  }, [token, canViewEquipment]);

  useEffect(() => {
    fetchEquipmentAlertCount();
  }, [fetchEquipmentAlertCount]);

  useEffect(() => {
    if (!showEquipment && !selectedEquipmentId) {
      fetchEquipmentAlertCount();
    }
    // eslint-disable-next-line
  }, [showEquipment, selectedEquipmentId]);

  // Leases needing attention — same shape as the equipment alert badge
  // above, but also keeps the full list (not just a count) since the
  // dashboard surfaces these prominently as their own section rather than
  // just a nav badge (see the "LEASES NEEDING ATTENTION" block below).
  const [atRiskLeases, setAtRiskLeases] = useState([]);
  const [atRiskLeasesLoading, setAtRiskLeasesLoading] = useState(false);
  // Distinguishes "org has zero leases" (neutral empty state) from "org has
  // leases but none are at risk" (the green all-clear) — see the "LEASES
  // NEEDING ATTENTION" block below.
  const [totalLeases, setTotalLeases] = useState(0);
  const canViewLeases = TAB_PERMISSIONS.LEASE.includes(user.role) && hasProduction;

  const fetchAtRiskLeases = useCallback(async () => {
    if (!canViewLeases) return;
    setAtRiskLeasesLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/leases/at-risk`,
        { headers: { Authorization: "Bearer " + token } },
      );
      setAtRiskLeases(res.data.leases);
      setTotalLeases(res.data.totalLeases || 0);
    } catch {
      // Non-critical — the section just stays empty/stale.
    } finally {
      setAtRiskLeasesLoading(false);
    }
    // eslint-disable-next-line
  }, [token, canViewLeases]);

  useEffect(() => {
    fetchAtRiskLeases();
  }, [fetchAtRiskLeases]);

  // Open AFEs over budget or >90% spent (GET /api/afes/attention) — same
  // "needs attention" shape as leases/certs. Budgets are a management
  // concern, so shown to the roles that can manage AFEs.
  const [afeAttention, setAfeAttention] = useState([]);
  const [afeOpenCount, setAfeOpenCount] = useState(0);
  const [afeAttentionLoading, setAfeAttentionLoading] = useState(false);
  const canSeeAfeAttention = canManageAfes(user);

  const fetchAfeAttention = useCallback(async () => {
    if (!canSeeAfeAttention) return;
    setAfeAttentionLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/afes/attention`, {
        headers: { Authorization: "Bearer " + token },
      });
      setAfeAttention(res.data.afes || []);
      setAfeOpenCount(res.data.totalOpen || 0);
    } catch {
      // Non-critical — the section just stays empty/stale.
    } finally {
      setAfeAttentionLoading(false);
    }
  }, [token, canSeeAfeAttention]);

  useEffect(() => {
    fetchAfeAttention();
  }, [fetchAfeAttention]);

  // Matches the backend's requireRole("admin", "supervisor") gate on
  // POST /api/wells/bulk-import exactly (routes/wells.js) — the Well Status
  // get-started card below only offers Bulk Import to roles that can
  // actually use it.
  const canBulkImportWells = user.role === "admin" || user.role === "supervisor";
  // "+ WELL" / "BULK IMPORT": the server lets only admins and supervisors add wells.
  const canAddWells = WELL_WRITE_ROLES.includes(user.role) && !trimmedMenu;

  // Pumpers: fetch the (lazy) Pumper Route screen's code right away while
  // there's signal, so the service worker has it cached and the route
  // still opens later out in the field with no connection.
  useEffect(() => {
    if (user.role === "field_hand") import("./PumperRoute").catch(() => {});
    // Same for a service company's crew and the rig report form.
    if (serviceCompany && user.role !== "consultant") import("./RigReports").catch(() => {});
  }, [user.role, serviceCompany]);

  // Third application of the same "at-risk" fetch shape as Equipment's
  // alert count and Lease's atRiskLeases above — org-wide, so gated to the
  // same admin/supervisor tier as the management view (a field_hand can
  // still see their OWN expiring cert via PersonnelCertifications.js
  // itself, just not this org-wide digest).
  const [atRiskCertifications, setAtRiskCertifications] = useState([]);
  const [atRiskCertificationsLoading, setAtRiskCertificationsLoading] = useState(false);
  // Distinguishes "org has zero certs on file" from "org has certs but none
  // are at risk" — see the "CERTIFICATIONS NEEDING ATTENTION" block below.
  const [totalCertifications, setTotalCertifications] = useState(0);
  const canManageCertifications = user.role === "admin" || user.role === "supervisor";

  const fetchAtRiskCertifications = useCallback(async () => {
    if (!canManageCertifications) return;
    setAtRiskCertificationsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/personnel-certifications/at-risk`,
        { headers: { Authorization: "Bearer " + token } },
      );
      setAtRiskCertifications(res.data.certifications);
      setTotalCertifications(res.data.totalCertifications || 0);
    } catch {
      // Non-critical — the section just stays empty/stale.
    } finally {
      setAtRiskCertificationsLoading(false);
    }
    // eslint-disable-next-line
  }, [token, canManageCertifications]);

  useEffect(() => {
    fetchAtRiskCertifications();
  }, [fetchAtRiskCertifications]);

  useEffect(() => {
    if (!showCertifications) {
      fetchAtRiskCertifications();
    }
    // eslint-disable-next-line
  }, [showCertifications]);

  // Portfolio-level Workover ROI summary — same admin/supervisor tier as
  // the backend's GET /api/wells/workover-roi-summary gate (real Expense
  // amounts per well, same tier as Leases/Accounting elsewhere on this
  // page). Descriptive-only, same as BHA Performance Analysis: no
  // prediction, just historical production/cost math — see the
  // disclaimer on WellIntelligence.jsx's WORKOVER ROI tab for the
  // full framing.
  const [workoverRoiSummary, setWorkoverRoiSummary] = useState(null);
  const [workoverRoiSummaryLoading, setWorkoverRoiSummaryLoading] = useState(false);
  const canViewWorkoverRoi = user.role === "admin" || user.role === "supervisor";

  const fetchWorkoverRoiSummary = useCallback(async () => {
    if (!canViewWorkoverRoi) return;
    setWorkoverRoiSummaryLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/wells/workover-roi-summary`,
        { headers: { Authorization: "Bearer " + token } },
      );
      setWorkoverRoiSummary(res.data);
    } catch {
      // Non-critical — the card just stays empty/stale.
    } finally {
      setWorkoverRoiSummaryLoading(false);
    }
    // eslint-disable-next-line
  }, [token, canViewWorkoverRoi]);

  useEffect(() => {
    fetchWorkoverRoiSummary();
  }, [fetchWorkoverRoiSummary]);

  // Compact "This Week" widget — same source endpoint as the full
  // Operations Calendar, just scoped to the next 7 days and trimmed to
  // critical/warning items so it's a quick glance rather than a second
  // full calendar crammed onto the dashboard. Gated by the same tier as
  // the CALENDAR tab itself (it surfaces the same AP/AR/lease data).
  const [thisWeekEvents, setThisWeekEvents] = useState([]);
  const [thisWeekLoading, setThisWeekLoading] = useState(false);
  const canViewCalendar = TAB_PERMISSIONS.CALENDAR.includes(user.role);

  const fetchThisWeek = useCallback(async () => {
    if (!canViewCalendar) return;
    setThisWeekLoading(true);
    try {
      const start = new Date();
      const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/calendar/events`,
        {
          headers: { Authorization: "Bearer " + token },
          params: { startDate: start.toISOString(), endDate: end.toISOString() },
        },
      );
      setThisWeekEvents(res.data.events.filter((e) => e.severity !== "info"));
    } catch {
      // Non-critical — the widget just stays empty/stale.
    } finally {
      setThisWeekLoading(false);
    }
    // eslint-disable-next-line
  }, [token, canViewCalendar]);

  useEffect(() => {
    fetchThisWeek();
  }, [fetchThisWeek]);

  useEffect(() => {
    if (!showCalendar) {
      fetchThisWeek();
    }
    // eslint-disable-next-line
  }, [showCalendar]);

  useEffect(() => {
    if (!showLeases) {
      fetchAtRiskLeases();
    }
    // eslint-disable-next-line
  }, [showLeases]);

  // Closes an open desktop nav group dropdown on an outside click, so it
  // behaves like a normal menu instead of staying open until another tab
  // is clicked.
  useEffect(() => {
    if (!openNavGroup) return;
    function handleClickOutside(e) {
      if (navGroupRef.current && !navGroupRef.current.contains(e.target)) {
        setOpenNavGroup(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openNavGroup]);

  const statusColor = {
    active: "#00f5ff",
    standby: "#ffaa00",
    completed: "#4466aa",
    "shut-in": "#ff4466",
  };
  const rigStatusColors = {
    Active: "#00f5ff",
    Standby: "#ffaa00",
    Moving: "#0080ff",
    "Down for Maintenance": "#ff4466",
    Released: "rgba(255,255,255,0.3)",
  };
  const cyan = "#00f5ff";
  const bg = "#020817";
  const card = "rgba(0,245,255,0.03)";
  const border = "rgba(0,245,255,0.15)";

  const navBtnStyle = (active) => ({
    color: active ? "#020817" : "rgba(0,245,255,0.7)",
    background: active
      ? "linear-gradient(135deg, #00f5ff, #0080ff)"
      : "rgba(0,245,255,0.05)",
    border: `1px solid ${active ? "transparent" : "rgba(0,245,255,0.2)"}`,
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "600",
    padding: isMobile ? "8px 12px" : "6px 16px",
    borderRadius: "20px",
    letterSpacing: "1px",
    whiteSpace: "nowrap",
    width: isMobile ? "100%" : "auto",
    textAlign: "left",
  });

  // ─── PAGE RENDERS ─────────────────────────────────────
  // The rig cards (with "+ Add rig"). allowDelete: the operator dashboard
  // keeps its delete button; rig services delete inside Rig Detail.
  const renderRigFleet = ({ allowDelete }) => (
        <div style={{ marginBottom: "28px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h3 style={{ fontSize: "17px", fontWeight: 700, color: "white", margin: 0 }}>
              {t("dashboard.main.rigFleet")}
            </h3>
            <button
              onClick={() => setShowRigForm(!showRigForm)}
              style={{
                background: showRigForm
                  ? "none"
                  : "linear-gradient(135deg, #00f5ff, #0080ff)",
                color: showRigForm ? "rgba(0,245,255,0.5)" : "#020817",
                border: `1px solid ${showRigForm ? border : "transparent"}`,
                fontWeight: "700",
                fontSize: "10px",
                letterSpacing: "2px",
                padding: "6px 16px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {showRigForm ? t("dashboard.main.cancel") : t("dashboard.main.addRig")}
            </button>
          </div>

          {showRigForm && (
            <div
              style={{
                backgroundColor: card,
                border: `1px solid ${border}`,
                borderRadius: "10px",
                padding: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                {[
                  {
                    label: t("dashboard.main.rigName"),
                    field: "name",
                    placeholder: t("dashboard.main.rigNamePlaceholder"),
                  },
                  {
                    label: t("dashboard.main.contractor"),
                    field: "contractor",
                    placeholder: t("dashboard.main.contractorPlaceholder"),
                  },
                ].map(({ label, field, placeholder }) => (
                  <div key={field}>
                    <label
                      style={{
                        color: cyan,
                        fontSize: "9px",
                        letterSpacing: "2px",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      {label}
                    </label>
                    <input
                      value={rigForm[field]}
                      onChange={(e) =>
                        setRigForm((f) => ({ ...f, [field]: e.target.value }))
                      }
                      placeholder={placeholder}
                      style={{
                        width: "100%",
                        backgroundColor: "rgba(0,245,255,0.05)",
                        border: "1px solid rgba(0,245,255,0.2)",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        color: "white",
                        fontSize: "13px",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                ))}
                <div>
                  <label
                    style={{
                      color: cyan,
                      fontSize: "9px",
                      letterSpacing: "2px",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    {t("dashboard.main.rigType")}
                  </label>
                  <select
                    value={rigForm.type}
                    onChange={(e) =>
                      setRigForm((f) => ({ ...f, type: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      backgroundColor: "#020817",
                      border: "1px solid rgba(0,245,255,0.2)",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      color: "white",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  >
                    {/* Stored values stay English; only the labels translate. */}
                    {[
                      ["Workover Unit", "workoverUnit"],
                      ["Drilling Rig", "drillingRig"],
                      ["Completion Unit", "completionUnit"],
                      ["Pump Truck", "pumpTruck"],
                      ["Coil Tubing Unit", "coilTubingUnit"],
                    ].map(([value, key]) => (
                      <option key={value} value={value}>
                        {t(`dashboard.main.rigTypes.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {rigError && (
                <p
                  style={{
                    color: "#ff4466",
                    fontSize: "12px",
                    marginBottom: "8px",
                  }}
                >
                  {rigError}
                </p>
              )}
              <button
                onClick={createRig}
                style={{
                  background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                  color: "#020817",
                  fontWeight: "700",
                  fontSize: "11px",
                  letterSpacing: "2px",
                  padding: "8px 20px",
                  borderRadius: "4px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {t("dashboard.main.registerRig")}
              </button>
            </div>
          )}

          {rigs.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                color: "rgba(0,245,255,0.3)",
                fontSize: "12px",
                letterSpacing: "3px",
                border: `1px solid ${border}`,
                borderRadius: "10px",
              }}
            >
              {t("dashboard.main.noRigs")}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "12px",
              }}
            >
              {rigs.map((rig) => {
                // Rig services: Working / Idle / Down for maintenance and the
                // current well from tour sheets and jobs (same as everywhere).
                const act = serviceCompany ? rig.activity : null;
                const sc = act ? { working: "#00cc66", idle: "rgba(255,255,255,0.6)", down: "#ff4466" }[act.status] : rigStatusColors[rig.status] || cyan;
                const shownWell = act ? act.currentWell?.wellName || "" : rig.currentWellName;
                const historyCount = act ? act.wellHistory.length : rig.history.length;
                return (
                  <div
                    key={rig._id}
                    onClick={() => setSelectedRig(rig)}
                    style={{
                      backgroundColor: card,
                      border: `1px solid ${border}`,
                      borderRadius: "10px",
                      padding: "16px",
                      cursor: "pointer",
                      transition: "border-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.borderColor =
                        "rgba(0,245,255,0.4)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.borderColor = border)
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: "white",
                            fontWeight: "700",
                            fontSize: "15px",
                            marginBottom: "2px",
                          }}
                        >
                          {rig.name}
                        </div>
                        <div
                          style={{
                            color: "rgba(0,245,255,0.4)",
                            fontSize: "10px",
                            letterSpacing: "2px",
                          }}
                        >
                          {rig.contractor.toUpperCase()}
                        </div>
                      </div>
                      <span
                        style={{
                          backgroundColor: sc + "22",
                          color: sc,
                          fontSize: "9px",
                          fontWeight: "700",
                          padding: "3px 8px",
                          borderRadius: "20px",
                          border: `1px solid ${sc}`,
                          letterSpacing: "1px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ● {act ? t(`serviceDash.state.${act.status}`) : t(`dashboard.main.rigStatus.${rig.status}`, { defaultValue: rig.status.toUpperCase() })}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "6px",
                        marginBottom: "10px",
                      }}
                    >
                      {[
                        { label: t("dashboard.main.rigType"), value: rig.type, muted: false },
                        {
                          label: t("dashboard.main.currentWell"),
                          value: shownWell || (act?.lastWell?.wellName ? t("dashboard.main.lastWell", { well: act.lastWell.wellName }) : t("dashboard.main.unassigned")),
                          muted: !shownWell,
                        },
                      ].map((s) => (
                        <div
                          key={s.label}
                          style={{
                            backgroundColor: "rgba(0,245,255,0.05)",
                            borderRadius: "4px",
                            padding: "6px 8px",
                          }}
                        >
                          <div
                            style={{
                              color: "rgba(0,245,255,0.4)",
                              fontSize: "8px",
                              letterSpacing: "1px",
                              marginBottom: "2px",
                            }}
                          >
                            {s.label}
                          </div>
                          <div
                            style={{
                              color: s.muted ? "rgba(255,255,255,0.2)" : "white",
                              fontSize: "11px",
                            }}
                          >
                            {s.value}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          color: "rgba(0,245,255,0.3)",
                          fontSize: "9px",
                          letterSpacing: "2px",
                        }}
                      >
                        {t("dashboard.main.rigHistory", { count: historyCount })}
                      </span>
                      {allowDelete && user.role === "admin" && (
                        <button
                          onClick={(e) => deleteRig(rig._id, e)}
                          style={{
                            background: "none",
                            border: "1px solid rgba(255,68,102,0.4)",
                            color: "rgba(255,68,102,0.7)",
                            fontSize: "9px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          {t("dashboard.main.delete")}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

  );

  if (showRigs && !selectedRig)
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#020817", fontFamily: "'Segoe UI', sans-serif" }}>
        <nav style={{ backgroundColor: "rgba(0,0,0,0.6)", borderBottom: `1px solid ${border}`, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ color: "white", fontSize: "20px", fontWeight: 700 }}>{trimmedMenu ? t("dashboard.rigsPage.titleEquipment") : t("dashboard.rigsPage.title")}</div>
          <button type="button" onClick={() => setShowRigs(false)} style={{ background: "none", border: `1px solid ${border}`, color: "rgba(0,245,255,0.7)", padding: "8px 14px", borderRadius: "6px", fontSize: "14px", cursor: "pointer" }}>
            {t("dashboard.rigsPage.back")}
          </button>
        </nav>
        <main style={{ padding: isMobile ? "16px" : "28px" }} data-testid="rigs-page">
          {trimmedMenu && TAB_PERMISSIONS.EQUIPMENT.includes(user.role) && (
            <div role="tablist" style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
              <button type="button" role="tab" aria-selected="true" style={{ background: "rgba(0,245,255,0.15)", border: `1px solid ${cyan}`, color: "white", borderRadius: "6px", minHeight: "44px", padding: "0 16px", fontSize: "14px" }}>
                {t("dashboard.rigsPage.tabRigs")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected="false"
                data-testid="rigs-equipment-tab"
                onClick={() => {
                  setShowRigs(false);
                  setEquipmentFromRigs(true);
                  setShowEquipment(true);
                }}
                style={{ background: "none", border: `1px solid ${border}`, color: "rgba(0,245,255,0.8)", borderRadius: "6px", minHeight: "44px", padding: "0 16px", fontSize: "14px", cursor: "pointer" }}
              >
                {t("dashboard.rigsPage.tabEquipment")}
                {equipmentAlertCount > 0 ? ` (${equipmentAlertCount})` : ""}
              </button>
            </div>
          )}
          {renderRigFleet({ allowDelete: false })}
        </main>
      </div>
    );

  if (showProcedureAnalyzer)
    return (
      <Suspense fallback={<PageFallback />}>
        <ProcedureAnalyzer
          token={token}
          wells={wells}
          userRole={user.role}
          onBack={() => setShowProcedureAnalyzer(false)}
        />
      </Suspense>
    );

  if (selectedRig)
    return (
      <RigDetail
        rig={selectedRig}
        token={token}
        wells={wells}
        user={user}
        // a rig-service company's rig is at its job's well (no hand-set well)
        hideAssignWell={trimmedMenu}
        onBack={() => setSelectedRig(null)}
        onUpdated={() => {
          fetchData();
          setSelectedRig(null);
        }}
      />
    );
  if (selectedWell)
    return (
      <WellDetail
        well={selectedWell}
        token={token}
        user={user}
        initialTab={wellDetailInitialTab}
        onBack={() => {
          setSelectedWell(null);
          setWellDetailInitialTab("overview");
        }}
        onNavigateToLease={
          canViewLeases
            ? (lease) => {
                setLeaseDeepLinkId(lease._id);
                setSelectedWell(null);
                setShowLeases(true);
              }
            : undefined
        }
      />
    );
  if (showForm)
    return (
      <NewReport
        token={token}
        wells={wells}
        onBack={() => setShowForm(false)}
        onSaved={() => {
          setShowForm(false);
          fetchData();
        }}
      />
    );
  if (showWellForm)
    return (
      <NewWell
        token={token}
        onBack={() => setShowWellForm(false)}
        onSaved={() => {
          setShowWellForm(false);
          fetchData();
        }}
      />
    );
  if (showBulkImport)
    return (
      <Suspense fallback={<PageFallback />}>
        <WellBulkImport
          token={token}
          onBack={() => setShowBulkImport(false)}
          onImported={fetchData}
        />
      </Suspense>
    );
  if (showSafety)
    return (
      <Suspense fallback={<PageFallback />}>
        <SafetyDashboard
          token={token}
          wells={wells}
          user={user}
          onBack={() => setShowSafety(false)}
        />
      </Suspense>
    );
  // Pipe tally is the one tally screen for every company; the older well
  // tallies (and their data) stay one tap away for companies that have them.
  if (showTally && !wellTallies)
    return (
      <Suspense fallback={<PageFallback />}>
        <PipeTally token={token} user={user} wells={wells} onBack={() => setShowTally(false)} onOpenWellTallies={rigOnly ? undefined : () => setWellTallies(true)} />
      </Suspense>
    );
  if (showTally)
    return (
      <Suspense fallback={<PageFallback />}>
        <TallySheet
          token={token}
          wells={wells}
          user={user}
          onBack={() => (wellTallies ? setWellTallies(false) : setShowTally(false))}
        />
      </Suspense>
    );
  if (showPumperRoute)
    return (
      <Suspense fallback={<PageFallback />}>
        <PumperRoute
          token={token}
          user={user}
          onBack={() => setShowPumperRoute(false)}
          onSetupTanks={(well) => {
            setShowPumperRoute(false);
            setWellDetailInitialTab("tanks");
            setSelectedWell(well);
          }}
          onOpenRouteSetup={
            TAB_PERMISSIONS.PUMPER_ROUTE_ADMIN.includes(user.role)
              ? () => {
                  setShowPumperRoute(false);
                  setShowPumperRouteAdmin(true);
                }
              : undefined
          }
        />
      </Suspense>
    );
  if (showLeaseOperating)
    return (
      <Suspense fallback={<PageFallback />}>
        <LeaseOperatingDashboard
          token={token}
          wells={wells}
          onBack={() => setShowLeaseOperating(false)}
        />
      </Suspense>
    );
  if (showPumperRouteAdmin)
    return (
      <Suspense fallback={<PageFallback />}>
        <PumperRouteAdmin
          token={token}
          wells={wells}
          onBack={() => setShowPumperRouteAdmin(false)}
          onPreviewAsPumper={() => {
            setShowPumperRouteAdmin(false);
            setShowPumperRoute(true);
          }}
        />
      </Suspense>
    );
  if (showFeedbackAdmin)
    return (
      <Suspense fallback={<PageFallback />}>
        <FeedbackAdmin
          token={token}
          onBack={() => setShowFeedbackAdmin(false)}
        />
      </Suspense>
    );
  if (showAccessManagement)
    return (
      <Suspense fallback={<PageFallback />}>
        <AccessManagement
          token={token}
          onBack={() => setShowAccessManagement(false)}
        />
      </Suspense>
    );
  if (showLegalCenter)
    return (
      <Suspense fallback={<PageFallback />}>
        <LegalCenter token={token} isAdmin={user.role === "admin"} onBack={() => setShowLegalCenter(false)} />
      </Suspense>
    );
  if (showVendor)
    return (
      <Suspense fallback={<PageFallback />}>
        <VendorDashboard
          token={token}
          wells={wells}
          user={user}
          onBack={() => setShowVendor(false)}
          onNavigateToAccounting={() => {
            setShowVendor(false);
            setShowAccounting(true);
          }}
        />
      </Suspense>
    );
  if (selectedEquipmentId)
    return (
      <Suspense fallback={<PageFallback />}>
        <EquipmentDetail
          equipmentId={selectedEquipmentId}
          token={token}
          canWrite={EQUIPMENT_WRITE_ROLES.includes(user.role)}
          onBack={() => setSelectedEquipmentId(null)}
        />
      </Suspense>
    );
  if (showEquipment)
    return (
      <Suspense fallback={<PageFallback />}>
        <EquipmentDashboard
          token={token}
          user={user}
          canWrite={EQUIPMENT_WRITE_ROLES.includes(user.role)}
          onBack={() => {
            setShowEquipment(false);
            if (equipmentFromRigs) {
              setEquipmentFromRigs(false);
              setShowRigs(true);
            }
          }}
          onSelectEquipment={(id) => setSelectedEquipmentId(id)}
        />
      </Suspense>
    );
  if (showAuditLog)
    return (
      <Suspense fallback={<PageFallback />}>
        <AuditLog
          token={token}
          onBack={() => setShowAuditLog(false)}
          onSelectWell={(well) => setSelectedWell(well)}
          onSelectEquipment={(id) => setSelectedEquipmentId(id)}
        />
      </Suspense>
    );
  if (showAnalytics)
    return (
      <Suspense fallback={<PageFallback />}>
        <AnalyticsDashboard
          token={token}
          wells={wells}
          onBack={() => setShowAnalytics(false)}
        />
      </Suspense>
    );
  if (showBhaPerformance)
    return (
      <Suspense fallback={<PageFallback />}>
        <BHAPerformanceAnalysis
          token={token}
          onBack={() => setShowBhaPerformance(false)}
        />
      </Suspense>
    );
  if (showGIS)
    return (
      <Suspense fallback={<PageFallback />}>
        <GISMap
          token={token}
          wells={wells}
          onBack={() => setShowGIS(false)}
          onSelectWell={(well) => {
            setShowGIS(false);
            setSelectedWell(well);
          }}
        />
      </Suspense>
    );
  if (showUserMgmt)
    return (
      <UserManagement token={token} onBack={() => setShowUserMgmt(false)} crewRoster={serviceOnly} />
    );
  if (showExpenseForm)
    return (
      <NewExpense
        token={token}
        wells={wells}
        onBack={() => setShowExpenseForm(false)}
        onSaved={() => {
          setShowExpenseForm(false);
          fetchData();
        }}
      />
    );
  if (showTicketScanner)
    return (
      <Suspense fallback={<PageFallback />}>
        <TicketScanner
          token={token}
          wells={wells}
          onTypeIn={
            trimmedMenu
              ? () => {
                  setShowTicketScanner(false);
                  setShowExpenseForm(true);
                }
              : undefined
          }
          onBack={() => {
            setShowTicketScanner(false);
            fetchData();
          }}
        />
      </Suspense>
    );
  if (showLeases)
    return (
      <Suspense fallback={<PageFallback />}>
        <LeaseManagement
          token={token}
          wells={wells}
          initialLeaseId={leaseDeepLinkId}
          onBack={() => {
            setShowLeases(false);
            setLeaseDeepLinkId(null);
          }}
        />
      </Suspense>
    );
  if (showFleet)
    return (
      <Suspense fallback={<PageFallback />}>
        <FleetDashboard token={token} user={user} onBack={() => setShowFleet(false)} />
      </Suspense>
    );
  if (showCostReview)
    return (
      <Suspense fallback={<PageFallback />}>
        <DuplicateReview token={token} user={user} onBack={() => setShowCostReview(false)} />
      </Suspense>
    );
  if (showAccounting)
    return (
      <Suspense fallback={<PageFallback />}>
        <AccountingOverview
          token={token}
          wells={wells}
          user={user}
          // Opens over Accounting; BACK on the review returns here.
          onOpenCostReview={() => setShowCostReview(true)}
          onBack={() => setShowAccounting(false)}
        />
      </Suspense>
    );
  if (showCalendar)
    return (
      <Suspense fallback={<PageFallback />}>
        <OperationsCalendar
          token={token}
          operatorFilters={!serviceOnly}
          onBack={() => setShowCalendar(false)}
          onNavigateToEquipment={(equipmentId) => {
            setShowCalendar(false);
            setSelectedEquipmentId(equipmentId);
          }}
          onNavigateToLease={(leaseId) => {
            setShowCalendar(false);
            setLeaseDeepLinkId(leaseId);
            setShowLeases(true);
          }}
          onNavigateToAccounting={() => {
            setShowCalendar(false);
            setShowAccounting(true);
          }}
          onNavigateToPersonnelCertification={(userId) => {
            setShowCalendar(false);
            setCertDeepLinkUserId(userId);
            setShowCertifications(true);
          }}
          onNavigateToJob={() => {
            setShowCalendar(false);
            setShowJobs(true);
          }}
          onNavigateToRig={(rigId) => {
            const rig = rigs.find((r) => String(r._id) === String(rigId));
            setShowCalendar(false);
            if (rig) setSelectedRig(rig);
            else setShowRigs(true);
          }}
        />
      </Suspense>
    );
  if (rigReportsView)
    return (
      <Suspense fallback={<PageFallback />}>
        <RigReports
          token={token}
          user={user}
          initialStatus={rigReportsView.status || ""}
          startNew={!!rigReportsView.startNew}
          pickRig={trimmedMenu}
          hideBillingSummary={trimmedMenu}
          onOpenSettings={trimmedMenu && user.role === "admin" ? () => setSettingsAt("company") : undefined}
          startPicking={!!rigReportsView.pick}
          startRigId={rigReportsView.rigId || ""}
          startDate={rigReportsView.date || ""}
          openReportId={rigReportsView.openReportId || null}
          onNewDailyReport={
            hasProduction
              ? () => {
                  setRigReportsView(null);
                  setShowForm(true);
                }
              : undefined
          }
          onBack={() => {
            setRigReportsView(null);
            fetchData();
          }}
        />
      </Suspense>
    );
  if (showJobs)
    return (
      <Suspense fallback={<PageFallback />}>
        <Jobs token={token} user={user} onBack={() => setShowJobs(false)} />
      </Suspense>
    );
  if (showCustomers)
    return (
      <Suspense fallback={<PageFallback />}>
        <Customers token={token} user={user} onBack={() => setShowCustomers(false)} />
      </Suspense>
    );
  if (showHydro)
    return (
      <Suspense fallback={<PageFallback />}>
        <HydroTickets token={token} user={user} initialView={typeof showHydro === "object" ? showHydro : null} onBack={() => setShowHydro(false)} onOpenSettings={trimmedMenu && user.role === "admin" ? () => setSettingsAt("hydro") : undefined} />
      </Suspense>
    );
  if (showPnl)
    return (
      <Suspense fallback={<PageFallback />}>
        <DivisionPnl token={token} user={user} onBack={() => setShowPnl(false)} />
      </Suspense>
    );
  if (settingsAt)
    return (
      <Suspense fallback={<PageFallback />}>
        <SettingsScreen
          token={token}
          user={user}
          initialSection={settingsAt === "open" ? "" : settingsAt}
          showAll={menuShowAll}
          onToggleShowAll={user.role === "admin" ? toggleShowAll : undefined}
          onOpenCustomers={() => {
            setSettingsAt("");
            setShowCustomers(true);
          }}
          onBack={() => setSettingsAt("")}
        />
      </Suspense>
    );
  if (showBilling)
    return (
      <Suspense fallback={<PageFallback />}>
        <Billing token={token} user={user} initialTab={typeof showBilling === "string" ? showBilling : undefined} onBack={() => setShowBilling(false)} />
      </Suspense>
    );
  if (showQb)
    return (
      <Suspense fallback={<PageFallback />}>
        <QuickBooksExport token={token} user={user} onBack={() => setShowQb(false)} />
      </Suspense>
    );
  if (showCertifications)
    return (
      <Suspense fallback={<PageFallback />}>
        <PersonnelCertifications
          token={token}
          user={user}
          initialUserId={certDeepLinkUserId}
          onBack={() => {
            setShowCertifications(false);
            setCertDeepLinkUserId(null);
          }}
        />
      </Suspense>
    );

  // `group` only affects which dropdown a tab is filed under on desktop —
  // it has no bearing on TAB_PERMISSIONS (still keyed by `id`, unchanged
  // below) or on any of these onClick handlers. COMMS has no group: it's
  // a same-page toggle (note `active: showChat`), not a navigation away
  // from Dashboard like every other tab, so it stays a standalone button
  // rather than living inside a dropdown.
  const navButtons = [
    {
      id: "RIG_REPORTS",
      label: t("dashboard.nav.rigReports"),
      group: null,
      onClick: () => {
        setRigReportsView({});
        setShowMobileMenu(false);
      },
    },
    {
      id: "RIGS",
      // Rigs & equipment in one place for a rig-service-only company.
      label: trimmedMenu ? t("dashboard.nav.rigsEquipment") : t("dashboard.nav.rigs"),
      badge: trimmedMenu && TAB_PERMISSIONS.EQUIPMENT.includes(user.role) && equipmentAlertCount > 0 ? equipmentAlertCount : null,
      group: null,
      onClick: () => {
        setShowRigs(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "HYDRO_TICKETS",
      label: t("dashboard.nav.hydroTickets"),
      group: null,
      onClick: () => {
        setShowHydro(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "JOBS",
      label: t("dashboard.nav.jobs"),
      group: "OPERATIONS",
      onClick: () => {
        setShowJobs(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "CUSTOMERS",
      label: t("dashboard.nav.customers"),
      group: "ADMIN",
      onClick: () => {
        setShowCustomers(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "SAFETY",
      label: t("dashboard.nav.safety"),
      group: "OPERATIONS",
      onClick: () => {
        setShowSafety(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "TALLY",
      label: t("dashboard.nav.pipeTally"),
      group: "OPERATIONS",
      onClick: () => {
        setShowTally(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "VENDOR",
      label: t("dashboard.nav.vendor"),
      group: "OPERATIONS",
      onClick: () => {
        setShowVendor(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "MAP",
      label: t("dashboard.nav.map"),
      group: "OPERATIONS",
      onClick: () => {
        setShowGIS(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "PUMPER_ROUTE",
      label: t("dashboard.nav.pumperRoute"),
      group: "OPERATIONS",
      onClick: () => {
        setShowPumperRoute(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "COMMS",
      label: t("dashboard.nav.comms"),
      group: null,
      onClick: () => {
        setShowChat(!showChat);
        setShowMobileMenu(false);
      },
      active: showChat,
    },
    {
      id: "USERS",
      label: t("dashboard.nav.users"),
      group: "ADMIN",
      onClick: () => {
        setShowUserMgmt(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "ANALYZE",
      label: t("dashboard.nav.analyze"),
      group: "INTELLIGENCE",
      onClick: () => {
        setShowProcedureAnalyzer(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "EQUIPMENT",
      label: t("dashboard.nav.equipment"),
      group: "ADMIN",
      badge: equipmentAlertCount > 0 ? equipmentAlertCount : null,
      onClick: () => {
        setShowEquipment(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "PUMPER_ROUTE_ADMIN",
      label: t("dashboard.nav.pumperRouteAdmin"),
      group: "ADMIN",
      onClick: () => {
        setShowPumperRouteAdmin(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "FEEDBACK_ADMIN",
      label: t("dashboard.nav.feedbackAdmin"),
      group: "ADMIN",
      onClick: () => {
        setShowFeedbackAdmin(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "ACCESS_MANAGEMENT",
      label: t("dashboard.nav.accessManagement"),
      group: "ADMIN",
      onClick: () => {
        setShowAccessManagement(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "AUDIT_LOG",
      label: t("dashboard.nav.auditLog"),
      group: "INTELLIGENCE",
      onClick: () => {
        setShowAuditLog(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "ANALYTICS",
      label: t("dashboard.nav.analytics"),
      group: "INTELLIGENCE",
      onClick: () => {
        setShowAnalytics(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "DIVISION_PNL",
      label: t("dashboard.nav.divisionPnl"),
      group: "INTELLIGENCE",
      onClick: () => {
        setShowPnl(true);
        setShowMobileMenu(false);
      },
    },
    {
      // Billing: Needs fixing · Ready to bill · Exported (rig + hydro tickets),
      // with the QuickBooks export and "Check before export" inside.
      id: "BILLING",
      label: t("dashboard.nav.billing"),
      group: "ADMIN",
      onClick: () => {
        setShowBilling(true);
        setShowMobileMenu(false);
      },
    },
    {
      // Settings in one place: company & reports, field tickets, price book,
      // catalog, customer rates, hydro, divisions & costs, QuickBooks names, menu.
      id: "SETTINGS",
      label: t("dashboard.nav.settings"),
      group: "ADMIN",
      onClick: () => {
        setSettingsAt("open");
        setShowMobileMenu(false);
      },
    },
    {
      id: "QUICKBOOKS",
      label: t("dashboard.nav.quickbooks"),
      group: "ADMIN",
      onClick: () => {
        setShowQb(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "BHA_PERFORMANCE",
      label: t("dashboard.nav.bhaPerformance"),
      group: "INTELLIGENCE",
      onClick: () => {
        setShowBhaPerformance(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "LEASE_OPERATING",
      label: t("dashboard.nav.leaseOperating"),
      group: "INTELLIGENCE",
      onClick: () => {
        setShowLeaseOperating(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "LEASE",
      label: t("dashboard.nav.leases"),
      group: "ADMIN",
      badge: atRiskLeases.length > 0 ? atRiskLeases.length : null,
      onClick: () => {
        setShowLeases(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "ACCOUNTING",
      label: t("dashboard.nav.accounting"),
      group: "ADMIN",
      onClick: () => {
        setShowAccounting(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "FLEET",
      label: t("dashboard.nav.fleet"),
      group: "ADMIN",
      onClick: () => {
        setShowFleet(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "COST_REVIEW",
      label: t("dashboard.nav.costReview"),
      group: "ADMIN",
      badge: costReviewCount?.open > 0 ? costReviewCount.open : null,
      onClick: () => {
        setShowCostReview(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "CALENDAR",
      label: t("dashboard.nav.calendar"),
      group: "ADMIN",
      onClick: () => {
        setShowCalendar(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "CERTIFICATIONS",
      label: t("dashboard.nav.certifications"),
      group: "OPERATIONS",
      badge: atRiskCertifications.length > 0 ? atRiskCertifications.length : null,
      onClick: () => {
        setShowCertifications(true);
        setShowMobileMenu(false);
      },
    },
    {
      id: "LEGAL_CENTER",
      label: t("dashboard.nav.legalCenter"),
      group: "OPERATIONS",
      onClick: () => {
        setShowLegalCenter(true);
        setShowMobileMenu(false);
      },
    },
  ];

  // "+ REPORT": a service company's report is the tour sheet; an operator
  // keeps the original daily report form.
  function openNewReport() {
    // A rig-service-only company: pick the rig — today's report opens or starts.
    if (serviceCompany && trimmedMenu) setRigReportsView({ pick: true });
    else if (serviceCompany) setRigReportsView({ startNew: true });
    else setShowForm(true);
  }
  // One "+ Receipt" (scan, or type it in) and "+ Rig report" for a
  // rig-service-only company; the rest keep "+ Expense" / "Scan receipt" /
  // "+ Report".
  const oneReceipt = trimmedMenu;
  const showNewReport = !(trimmedMenu && !serviceCompany);
  const newReportLabel = trimmedMenu ? t("dashboard.nav.addRigReport") : t("dashboard.nav.addReport");

  // No customer org's admin should ever see the demo-access tool, even
  // though role-wise they'd otherwise qualify — matches the backend's
  // requireDemoOrg gate on /api/demo-access.
  const isDemoOrg = user.organizationId?.name === "RigIQ Demo Co";
  const visibleNavButtons = navButtons.filter((btn) => {
    if (!TAB_PERMISSIONS[btn.id]?.includes(user.role)) return false;
    if (btn.id === "ACCESS_MANAGEMENT" && !isDemoOrg) return false;
    // Equipment lives under Rigs & equipment (still its own screen).
    if (btn.id === "EQUIPMENT" && trimmedMenu) return false;
    // Billing is for a rig-service / hydro company; it holds the QuickBooks export.
    if (btn.id === "BILLING" && !serviceOnly) return false;
    if (btn.id === "SETTINGS" && !serviceOnly) return false;
    if (btn.id === "QUICKBOOKS" && trimmedMenu) return false;
    if (btn.id === "COST_REVIEW" && !(costReviewCount?.total > 0 || costReviewCount?.migrated > 0)) return false;
    if (tabHiddenForOrg(menuUser, btn.id)) return false;
    return true;
  }).map((btn) => {
    // Orgs with both modules get PRODUCTION and RIG SERVICES nav groups.
    const moduleGroup = moduleGroupFor(user, btn.id);
    return moduleGroup ? { ...btn, group: moduleGroup } : btn;
  });
  // A short menu: the role's items first, the rest under More.
  const primaryIds = trimmedMenu ? shortMenuFor(user) : null;
  const navList = primaryIds
    ? [
        ...visibleNavButtons.filter((b) => primaryIds.includes(b.id)).sort((a, b) => primaryIds.indexOf(a.id) - primaryIds.indexOf(b.id)).map((b) => ({ ...b, group: undefined })),
        ...visibleNavButtons.filter((b) => !primaryIds.includes(b.id)).map((b) => ({ ...b, group: "MORE" })),
      ]
    : visibleNavButtons;
  const standaloneNavButtons = navList.filter((btn) => !btn.group);
  const groupOrder = primaryIds ? ["MORE"] : bothModules ? ["PRODUCTION", "RIG_SERVICES", ...NAV_GROUP_ORDER] : NAV_GROUP_ORDER;
  const navGroups = groupOrder.map((groupId) => ({
    id: groupId,
    label: t(NAV_GROUP_LABELS[groupId]),
    items: navList.filter((btn) => btn.group === groupId),
  })).filter((g) => g.items.length > 0);

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: bg,
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* GLOBAL STORM ALERT */}
      {globalAlert && (
        <div
          style={{
            backgroundColor: globalAlert.color + "22",
            borderBottom: `2px solid ${globalAlert.color}`,
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span style={{ fontSize: "20px" }}>⚡</span>
          <div style={{ flex: 1 }}>
            <span
              style={{
                color: globalAlert.color,
                fontWeight: "700",
                fontSize: "11px",
                letterSpacing: "2px",
              }}
            >
              {t(`dashboard.main.lightning.${globalAlert.level}`, { defaultValue: globalAlert.level })} — {globalAlert.wellName}:
            </span>
            <span
              style={{
                color: globalAlert.color,
                fontSize: "11px",
                marginLeft: "6px",
                opacity: 0.9,
              }}
            >
              {riskMessage(t, globalAlert)}
            </span>
          </div>
          <button
            onClick={() => setGlobalAlert(null)}
            style={{
              background: "none",
              border: "none",
              color: globalAlert.color,
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* NAV */}
      <nav
        style={{
          backgroundColor: "rgba(0,0,0,0.6)",
          borderBottom: `1px solid ${border}`,
          backdropFilter: "blur(10px)",
        }}
      >
        {/* TOP ROW */}
        <div
          style={{
            padding: isMobile ? "10px 16px" : "10px 28px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid rgba(0,245,255,0.08)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src="/rigiq-logo.png"
              alt="RigIQ"
              style={{
                height: isMobile ? "36px" : "42px",
                width: "auto",
                flexShrink: 0,
                filter: "drop-shadow(0 0 8px rgba(0,245,255,0.3))",
                mixBlendMode: "screen",
              }}
            />
            {!isMobile && (
              <>
                <div
                  style={{
                    width: "1px",
                    height: "20px",
                    backgroundColor: border,
                  }}
                />
                <div
                  style={{
                    color: "rgba(0,245,255,0.5)",
                    fontSize: "11px",
                    letterSpacing: "3px",
                  }}
                >
                  {t("dashboard.nav.fieldOps")}
                </div>
              </>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {!isMobile && user.organizationId?.name && (
              <>
                <span
                  title={t("dashboard.main.organization")}
                  style={{
                    color: "rgba(0,245,255,0.5)",
                    fontSize: "11px",
                    letterSpacing: "1px",
                  }}
                >
                  {user.organizationId.name}
                </span>
                <div
                  style={{
                    width: "1px",
                    height: "16px",
                    backgroundColor: border,
                  }}
                />
              </>
            )}
            {!isMobile && (
              <>
                <span
                  style={{ color: "rgba(255,255,255,0.7)", fontSize: "13px" }}
                >
                  {user.name}
                </span>
                <span
                  style={{
                    background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: "#020817",
                    fontSize: "10px",
                    fontWeight: "700",
                    padding: "3px 10px",
                    borderRadius: "20px",
                    letterSpacing: "1px",
                  }}
                >
                  {user.role.toUpperCase()}
                </span>
              </>
            )}
            {!isMobile && <LanguageToggle compact />}
            {/* On a phone, SMS Alerts / Feedback / EN-ES move to their own
                row below (MOBILE TOOLBAR) — the single top row used to
                overflow at 360-390px, putting LOGOUT on top of the ☰ so a
                normal tap couldn't open the menu. */}
            {!isMobile && <SmsSettingsButton token={token} user={user} />}
            {!isMobile && <FeedbackButton token={token} />}
            {!isMobile && <HelpButton />}
            {!isMobile && serviceOnly && <AccountMenu isAdmin={user.role === "admin"} onOpenMenuSettings={() => setSettingsAt("menu")} onOpenLegal={() => setShowLegalCenter(true)} />}
            <button
              onClick={onLogout}
              style={{
                color: "rgba(255,68,102,0.7)",
                background: "none",
                border: "1px solid rgba(255,68,102,0.3)",
                cursor: "pointer",
                fontSize: isMobile ? "12px" : "11px",
                padding: isMobile ? "0 14px" : "4px 12px",
                minHeight: isMobile ? "44px" : undefined,
                borderRadius: "4px",
                letterSpacing: "1px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.logout")}
            </button>
            {/* HAMBURGER */}
            {isMobile && (
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                aria-label={t("header.menu")}
                aria-expanded={showMobileMenu}
                style={{
                  background: "none",
                  border: `1px solid ${border}`,
                  color: cyan,
                  cursor: "pointer",
                  fontSize: "20px",
                  width: "44px",
                  height: "44px",
                  padding: 0,
                  borderRadius: "4px",
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                {showMobileMenu ? "✕" : "☰"}
              </button>
            )}
          </div>
        </div>

        {/* MOBILE TOOLBAR — every control >= 44px tall, nothing overlaps */}
        {isMobile && (
          <div
            data-testid="mobile-toolbar"
            style={{
              padding: "8px 16px",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto auto",
              gap: "8px",
              alignItems: "center",
              borderBottom: `1px solid rgba(0,245,255,0.08)`,
            }}
          >
            <SmsSettingsButton token={token} user={user} buttonStyle={mobileToolbarBtn} />
            <FeedbackButton token={token} buttonStyle={mobileToolbarBtn} />
            <LanguageToggle large />
            <HelpButton buttonStyle={{ minWidth: "44px", minHeight: "44px" }} />
          </div>
        )}

        {/* MOBILE DROPDOWN MENU */}
        {isMobile && showMobileMenu && (
          <div
            style={{
              backgroundColor: "rgba(2,8,23,0.98)",
              borderBottom: `1px solid ${border}`,
              padding: "12px 16px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              maxHeight: "calc(100vh - 70px)",
              overflowY: "auto",
            }}
          >
            {standaloneNavButtons.map((btn) => (
              <button
                key={btn.label}
                onClick={btn.onClick}
                style={navBtnStyle(btn.active)}
              >
                {btn.label}
                {btn.badge != null && <NavBadge count={btn.badge} />}
              </button>
            ))}
            {navGroups.map((group) => (
              <div key={group.id}>
                {group.id === "MORE" ? (
                  <button type="button" data-testid="phone-more" aria-expanded={phoneMoreOpen} onClick={() => setPhoneMoreOpen(!phoneMoreOpen)} style={navBtnStyle(phoneMoreOpen)}>
                    {group.label} {phoneMoreOpen ? "▴" : "▾"}
                  </button>
                ) : (
                <div
                  style={{
                    color: "rgba(0,245,255,0.35)",
                    fontSize: "9px",
                    letterSpacing: "2px",
                    fontWeight: "700",
                    padding: "6px 4px 2px",
                  }}
                >
                  {group.label}
                </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: group.id === "MORE" ? "8px" : 0 }}>
                  {(group.id !== "MORE" || phoneMoreOpen) && group.items.map((btn) => (
                    <button key={btn.label} onClick={btn.onClick} style={navBtnStyle(btn.active)}>
                      {btn.label}
                      {btn.badge != null && <NavBadge count={btn.badge} />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div
              style={{
                borderTop: `1px solid ${border}`,
                paddingTop: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {canAddWells && (
              <button
                onClick={() => {
                  setShowWellForm(true);
                  setShowMobileMenu(false);
                }}
                style={{
                  ...navBtnStyle(false),
                  color: cyan,
                  border: `1px solid ${cyan}`,
                }}
              >
                {t("dashboard.nav.addWell")}
              </button>
              )}
              {canAddWells && (
              <button
                onClick={() => {
                  setShowBulkImport(true);
                  setShowMobileMenu(false);
                }}
                style={{ ...navBtnStyle(false), color: "rgba(0,245,255,0.7)" }}
              >
                {t("dashboard.nav.bulkImportWells")}
              </button>
              )}
              {canWriteExpenses(user) && !oneReceipt && (
                <button
                  onClick={() => {
                    setShowExpenseForm(true);
                    setShowMobileMenu(false);
                  }}
                  style={{ ...navBtnStyle(false), color: "rgba(0,245,255,0.7)" }}
                >
                  {t("dashboard.nav.addExpense")}
                </button>
              )}
              {canWriteExpenses(user) && (
                <button
                  onClick={() => {
                    setShowTicketScanner(true);
                    setShowMobileMenu(false);
                  }}
                  style={{ ...navBtnStyle(false), color: "rgba(0,245,255,0.7)" }}
                >
                  {oneReceipt ? t("dashboard.nav.addReceipt") : t("dashboard.nav.scanTicket")}
                </button>
              )}
              {showNewReport && (
              <button
                onClick={() => {
                  openNewReport();
                  setShowMobileMenu(false);
                }}
                style={{
                  ...navBtnStyle(false),
                  background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                  color: "#020817",
                  border: "none",
                  fontWeight: "700",
                }}
              >
                {newReportLabel}
              </button>
              )}
              {serviceOnly && (
                <div data-testid="phone-account" style={{ borderTop: `1px solid ${border}`, paddingTop: "8px" }}>
                  <AccountMenu
                    inline
                    isAdmin={user.role === "admin"}
                    onOpenMenuSettings={() => {
                      setSettingsAt("menu");
                      setShowMobileMenu(false);
                    }}
                    onOpenLegal={() => {
                      setShowLegalCenter(true);
                      setShowMobileMenu(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* DESKTOP BOTTOM ROW */}
        {!isMobile && (
          <div
            ref={navGroupRef}
            style={{
              padding: "8px 28px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexWrap: "wrap",
              rowGap: "8px",
            }}
          >
            {standaloneNavButtons.map((btn) => (
              <button
                key={btn.label}
                onClick={btn.onClick}
                style={navBtnStyle(btn.active)}
                onMouseEnter={(e) => {
                  if (!btn.active) {
                    e.currentTarget.style.background = "rgba(0,245,255,0.12)";
                    e.currentTarget.style.borderColor = "rgba(0,245,255,0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!btn.active) {
                    e.currentTarget.style.background = "rgba(0,245,255,0.05)";
                    e.currentTarget.style.borderColor = "rgba(0,245,255,0.2)";
                  }
                }}
              >
                {btn.label}
                {btn.badge != null && <NavBadge count={btn.badge} />}
              </button>
            ))}
            {navGroups.map((group) => (
              <NavGroupDropdown
                key={group.id}
                group={group}
                isOpen={openNavGroup === group.id}
                onToggle={() => setOpenNavGroup(openNavGroup === group.id ? null : group.id)}
                onSelect={(btn) => {
                  btn.onClick();
                  setOpenNavGroup(null);
                }}
                navBtnStyle={navBtnStyle}
              />
            ))}
            <div style={{ flex: 1 }} />
            {canAddWells && (
            <button
              onClick={() => setShowWellForm(true)}
              style={{
                background: "none",
                border: `1px solid ${cyan}`,
                color: cyan,
                fontWeight: "700",
                fontSize: "10px",
                letterSpacing: "2px",
                padding: "6px 14px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {t("dashboard.nav.addWell")}
            </button>
            )}
            {canAddWells && (
            <button
              onClick={() => setShowBulkImport(true)}
              style={{
                background: "none",
                border: "1px solid rgba(0,245,255,0.4)",
                color: "rgba(0,245,255,0.7)",
                fontWeight: "700",
                fontSize: "10px",
                letterSpacing: "2px",
                padding: "6px 14px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {t("dashboard.nav.bulkImportWells")}
            </button>
            )}
            {canWriteExpenses(user) && !oneReceipt && (
              <button
                onClick={() => setShowExpenseForm(true)}
                style={{
                  background: "none",
                  border: "1px solid rgba(0,245,255,0.4)",
                  color: "rgba(0,245,255,0.7)",
                  fontWeight: "700",
                  fontSize: "10px",
                  letterSpacing: "2px",
                  padding: "6px 14px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {t("dashboard.nav.addExpense")}
              </button>
            )}
            {canWriteExpenses(user) && (
              <button
                onClick={() => setShowTicketScanner(true)}
                style={{
                  background: "none",
                  border: "1px solid rgba(0,245,255,0.4)",
                  color: "rgba(0,245,255,0.7)",
                  fontWeight: "700",
                  fontSize: "10px",
                  letterSpacing: "2px",
                  padding: "6px 14px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {oneReceipt ? t("dashboard.nav.addReceipt") : t("dashboard.nav.scanTicket")}
              </button>
            )}
            {showNewReport && (
            <button
              onClick={openNewReport}
              style={{
                background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                color: "#020817",
                fontWeight: "700",
                fontSize: "10px",
                letterSpacing: "2px",
                padding: "6px 16px",
                borderRadius: "4px",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 0 12px rgba(0,245,255,0.25)",
              }}
            >
              {newReportLabel}
            </button>
            )}
          </div>
        )}
      </nav>

      <main style={{ padding: isMobile ? "16px" : "28px" }}>
        {/* HEADER — rig services: plain sentence-case title with the rig count */}
        {serviceCompany ? (
          <div style={{ marginBottom: "18px" }}>
            <h2 style={{ fontSize: isMobile ? "22px" : "26px", fontWeight: 700, color: "white", margin: "0 0 4px" }}>{t("dashboard.main.rigTitle")}</h2>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", margin: 0 }}>
              {serviceData && !crewHome && !hydroHome ? t("dashboard.main.rigSubtitle", { count: (serviceData.rigs || []).length, date: formatDate(serviceData.today) }) : serviceData ? formatDate(serviceData.today) : ""}
            </p>
          </div>
        ) : (
        <div style={{ marginBottom: "24px" }}>
          <h2
            style={{
              fontSize: isMobile ? "20px" : "26px",
              fontWeight: "800",
              letterSpacing: "2px",
              color: "white",
              marginBottom: "4px",
            }}
          >
            {t("dashboard.main.title")}
          </h2>
          <p
            style={{
              color: "rgba(0,245,255,0.5)",
              fontSize: "12px",
              letterSpacing: "3px",
            }}
          >
            {t("dashboard.main.subtitle", { count: wellsTotal })}
          </p>
        </div>
        )}

        {/* The role home: the next action first (toolpusher, crew, hydro operator, office) */}
        {trimmedMenu && (
          <RoleHome
            user={user}
            token={token}
            onOpenReport={(id) => setRigReportsView({ openReportId: id })}
            onStartReport={(rigId, date) => setRigReportsView({ startNew: true, rigId, date })}
            onPickRig={() => setRigReportsView({ pick: true })}
            onOpenHydro={(id) => setShowHydro({ open: id })}
            onNewHydro={() => setShowHydro({ newTicket: true })}
            onOpenBilling={(tab) => setShowBilling(tab)}
          />
        )}

        {serviceCompany && !hydroHome && (
          <ServiceDashboardPanel
            crewView={crewHome}
            token={token}
            user={user}
            data={serviceData}
            onOpenReports={() => setRigReportsView({ status: "submitted" })}
            onOpenJobs={() => setShowJobs(true)}
            onOpenCerts={() => setShowCertifications(true)}
            onNewReport={WRITE_REPORT_ROLES.includes(user.role) ? (rigId, date) => setRigReportsView({ startNew: true, rigId: rigId || "", date: date || "" }) : undefined}
            onOpenReport={(id) => setRigReportsView({ openReportId: id })}
            onOpenRig={(id) => {
              const rig = rigs.find((r) => String(r._id) === String(id));
              if (rig) setSelectedRig(rig);
              else setShowRigs(true);
            }}
            onOpenRigs={() => setShowRigs(true)}
            onOpenCustomers={TAB_PERMISSIONS.CUSTOMERS?.includes(user.role) ? () => setShowCustomers(true) : undefined}
            onOpenCalendar={canViewCalendar ? () => setShowCalendar(true) : undefined}
          />
        )}

        {loading && (
          <div style={{ marginBottom: "16px" }}>
            <LoadingLine label={t("dashboard.main.loadingSystems")} />
          </div>
        )}
        {error && <ErrorBanner message={error} onRetry={fetchData} />}

        {/* THIS WEEK — compact cross-module summary (maintenance, compliance,
            leases, AP, AR) for the next 7 days, kept to one line so it doesn't
            compete with the fuller LEASES NEEDING ATTENTION section below;
            click through to the full Operations Calendar for the month view. */}
        {!serviceCompany && canViewCalendar && !thisWeekLoading && thisWeekEvents.length > 0 && (
          <div
            onClick={() => setShowCalendar(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              backgroundColor: "rgba(0,245,255,0.04)",
              border: `1px solid ${border}`,
              borderRadius: "8px",
              padding: "10px 16px",
              marginBottom: "16px",
              cursor: "pointer",
            }}
          >
            <span style={{ color: cyan, fontSize: "11px", fontWeight: "700", letterSpacing: "2px" }}>
              {t("dashboard.main.thisWeek")}
            </span>
            <span style={{ color: "#ff4466", fontSize: "12px", fontWeight: "700" }}>
              {t("dashboard.main.critical", { count: thisWeekEvents.filter((e) => e.severity === "critical").length })}
            </span>
            <span style={{ color: "#ffaa00", fontSize: "12px", fontWeight: "700" }}>
              {t("dashboard.main.warning", { count: thisWeekEvents.filter((e) => e.severity === "warning").length })}
            </span>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px", flex: 1 }}>
              {thisWeekEvents.slice(0, 2).map((e) => e.title).join(" · ")}
              {thisWeekEvents.length > 2 ? ` · ${t("dashboard.main.more", { count: thisWeekEvents.length - 2 })}` : ""}
            </span>
            <span style={{ color: "rgba(0,245,255,0.5)", fontSize: "11px", letterSpacing: "1px" }}>
              {t("dashboard.main.viewCalendar")}
            </span>
          </div>
        )}

        {/* WORKOVER ROI — portfolio-level rollup of GET
            /api/wells/workover-roi-summary. Descriptive only (see the full
            disclaimer on WellIntelligence.jsx's WORKOVER ROI tab, where each
            well's individual events are broken out) — this card is just the
            headline number for a quick glance across the whole portfolio. */}
        {!rigOnly && canViewWorkoverRoi && !workoverRoiSummaryLoading && workoverRoiSummary?.concludedEventsCount > 0 && (
          <div
            style={{
              backgroundColor: "rgba(0,204,102,0.05)",
              border: "1px solid rgba(0,204,102,0.25)",
              borderRadius: "10px",
              padding: "16px 20px",
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div>
              <div style={{ color: "#00cc66", fontSize: "11px", fontWeight: "700", letterSpacing: "2px", marginBottom: "4px" }}>
                {t("dashboard.main.roiTitle", { year: workoverRoiSummary.year })}
              </div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "10px" }}>
                {t("dashboard.main.roiAnalyzed", { count: workoverRoiSummary.concludedEventsCount })}
                {workoverRoiSummary.insufficientDataCount > 0
                  ? ` ${t("dashboard.main.roiInsufficient", { count: workoverRoiSummary.insufficientDataCount })}`
                  : ""}
                {workoverRoiSummary.avgPayoutDays != null
                  ? ` · ${t("dashboard.main.roiPayout", { days: Math.round(workoverRoiSummary.avgPayoutDays) })}`
                  : ""}{" "}
                · {t("dashboard.main.roiDisclaimer")}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  color:
                    workoverRoiSummary.totalIncrementalRevenue180ThisYear >= 0 ? "#00cc66" : "#ff4466",
                  fontSize: "22px",
                  fontWeight: "800",
                }}
              >
                {workoverRoiSummary.totalIncrementalRevenue180ThisYear >= 0 ? "" : "-"}$
                {Math.abs(Math.round(workoverRoiSummary.totalIncrementalRevenue180ThisYear)).toLocaleString()}
              </div>
              <div style={{ color: "rgba(0,245,255,0.4)", fontSize: "9px", letterSpacing: "1px" }}>
                {t("dashboard.main.roiRevenue")}
              </div>
            </div>
          </div>
        )}

        {/* LEASES NEEDING ATTENTION — same pattern as
            LeaseOperatingDashboard's "WELLS NEEDING ATTENTION" section:
            surfaced on the main dashboard rather than buried inside the
            Lease Management page, so an expiring/expired lease is seen
            without navigating there first. */}
        {!rigOnly && canViewLeases && (
          <div style={{ marginBottom: "28px" }}>
            <h3
              style={{
                fontSize: "13px",
                letterSpacing: "3px",
                color: "#ff4466",
                marginBottom: "12px",
              }}
            >
              {t("dashboard.alerts.leasesTitle")}
            </h3>
            {atRiskLeasesLoading ? (
              <LoadingLine label={t("dashboard.main.loading")} />
            ) : totalLeases === 0 ? (
              <div
                style={{
                  color: "rgba(255,255,255,0.4)",
                  fontSize: "12px",
                  padding: "16px",
                  textAlign: "center",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "10px",
                  backgroundColor: "rgba(255,255,255,0.02)",
                }}
              >
                {t("dashboard.emptyStates.noLeasesYet")}
              </div>
            ) : atRiskLeases.length === 0 ? (
              <div
                style={{
                  color: "rgba(0,204,102,0.7)",
                  fontSize: "12px",
                  padding: "16px",
                  textAlign: "center",
                  border: "1px solid rgba(0,204,102,0.2)",
                  borderRadius: "10px",
                  backgroundColor: "rgba(0,204,102,0.03)",
                }}
              >
                {t("dashboard.emptyStates.noLeasesAtRisk")}
              </div>
            ) : (
              atRiskLeases.map((lease) => {
                const isExpired = lease.riskStatus === "expired";
                const color = isExpired ? "#ff4466" : "#ffaa00";
                return (
                  <div
                    key={lease._id}
                    onClick={() => setShowLeases(true)}
                    style={{
                      backgroundColor: isExpired
                        ? "rgba(255,68,102,0.05)"
                        : "rgba(255,170,0,0.05)",
                      border: `1px solid ${color}66`,
                      borderRadius: "8px",
                      padding: "12px 14px",
                      marginBottom: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "6px",
                      }}
                    >
                      <span style={{ color: "white", fontWeight: "700", fontSize: "13px" }}>
                        {lease.lessorName}
                      </span>
                      <span
                        style={{
                          color,
                          fontSize: "11px",
                          fontWeight: "700",
                          letterSpacing: "1px",
                        }}
                      >
                        {expiryLabel(lease.primaryTermEndDate, t)}
                      </span>
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.4)",
                        fontSize: "11px",
                        marginTop: "4px",
                      }}
                    >
                      {t("dashboard.alerts.primaryTermEnds", {
                        // A calendar date stored at UTC midnight — format it in
                        // UTC, or it shows as the previous day west of UTC.
                        date: formatDateUTC(lease.primaryTermEndDate),
                      })}
                      {lease.legalDescription ? ` · ${lease.legalDescription}` : ""}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* AFES OVER BUDGET / >90% SPENT — open AFEs only, worst first.
            Tapping one opens that well's Expenses tab (AFE card). Hidden
            entirely while the org has no open AFEs. */}
        {!serviceCompany && canSeeAfeAttention && afeOpenCount > 0 && (
          <div style={{ marginBottom: "28px" }} data-testid="afe-attention">
            <h3 style={{ fontSize: "13px", letterSpacing: "3px", color: "#ff4466", marginBottom: "12px" }}>
              {t("afe.attention.title")}
            </h3>
            {afeAttentionLoading ? (
              <LoadingLine label={t("dashboard.main.loading")} />
            ) : afeAttention.length === 0 ? (
              <div
                style={{
                  color: "rgba(0,204,102,0.7)",
                  fontSize: "12px",
                  padding: "16px",
                  textAlign: "center",
                  border: "1px solid rgba(0,204,102,0.2)",
                  borderRadius: "10px",
                  backgroundColor: "rgba(0,204,102,0.03)",
                }}
              >
                {t("afe.attention.allClear")}
              </div>
            ) : (
              afeAttention.map((a) => {
                const color = afeStatusColor(a.summary.status);
                const well = wells.find((w) => String(w._id || w.id) === String(a.wellId));
                return (
                  <div
                    key={a._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (!well) return;
                      setWellDetailInitialTab("expenses");
                      setSelectedWell(well);
                    }}
                    style={{
                      backgroundColor: `${color}0d`,
                      border: `1px solid ${color}66`,
                      borderRadius: "8px",
                      padding: "12px 14px",
                      marginBottom: "8px",
                      cursor: well ? "pointer" : "default",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "6px" }}>
                      <span style={{ color: "white", fontWeight: "700", fontSize: "13px" }}>{a.afeNumber}</span>
                      <span style={{ color, fontSize: "11px", fontWeight: "700", letterSpacing: "1px" }}>
                        {t(`afe.statusFlag.${a.summary.status}`)}
                      </span>
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", marginTop: "4px" }}>
                      {t("afe.attention.row", { well: a.wellName, pct: a.summary.pctSpent })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* CERTIFICATIONS NEEDING ATTENTION — same pattern as LEASES
            NEEDING ATTENTION above (third application of the same
            overdue/expiring shape: Equipment, Lease, now Personnel
            Certifications). Org-wide, so admin/supervisor only — a
            field_hand sees their own expiring cert inside
            PersonnelCertifications.js itself instead. */}
        {/* (rig services: the one certifications widget is the dashboard card) */}
        {!serviceCompany && canManageCertifications && (
          <div style={{ marginBottom: "28px" }}>
            <h3
              style={{
                fontSize: "13px",
                letterSpacing: "3px",
                color: "#ff4466",
                marginBottom: "12px",
              }}
            >
              {t("dashboard.alerts.certsTitle")}
            </h3>
            {atRiskCertificationsLoading ? (
              <LoadingLine label={t("dashboard.main.loading")} />
            ) : totalCertifications === 0 ? (
              <div
                style={{
                  color: "rgba(255,255,255,0.4)",
                  fontSize: "12px",
                  padding: "16px",
                  textAlign: "center",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "10px",
                  backgroundColor: "rgba(255,255,255,0.02)",
                }}
              >
                {t("dashboard.emptyStates.noCertsYet")}
              </div>
            ) : atRiskCertifications.length === 0 ? (
              <div
                style={{
                  color: "rgba(0,204,102,0.7)",
                  fontSize: "12px",
                  padding: "16px",
                  textAlign: "center",
                  border: "1px solid rgba(0,204,102,0.2)",
                  borderRadius: "10px",
                  backgroundColor: "rgba(0,204,102,0.03)",
                }}
              >
                {t("dashboard.emptyStates.noCertsAtRisk")}
              </div>
            ) : (
              atRiskCertifications.map((cert) => {
                const isExpired = cert.status === "expired";
                const color = isExpired ? "#ff4466" : "#ffaa00";
                return (
                  <div
                    key={cert._id}
                    onClick={() => {
                      setCertDeepLinkUserId(cert.userId);
                      setShowCertifications(true);
                    }}
                    style={{
                      backgroundColor: isExpired
                        ? "rgba(255,68,102,0.05)"
                        : "rgba(255,170,0,0.05)",
                      border: `1px solid ${color}66`,
                      borderRadius: "8px",
                      padding: "12px 14px",
                      marginBottom: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "6px",
                      }}
                    >
                      <span style={{ color: "white", fontWeight: "700", fontSize: "13px" }}>
                        {cert.userName}
                      </span>
                      <span
                        style={{
                          color,
                          fontSize: "11px",
                          fontWeight: "700",
                          letterSpacing: "1px",
                        }}
                      >
                        {expiryLabel(cert.expirationDate, t)}
                      </span>
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.4)",
                        fontSize: "11px",
                        marginTop: "4px",
                      }}
                    >
                      {t(`certTypes.${cert.certificationType}`, { defaultValue: cert.certificationType.replace(/_/g, " ") })}
                      {cert.certificationName ? ` · ${cert.certificationName}` : ""}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* RIG FLEET — rig services: on the Rigs page instead */}
        {!serviceCompany && renderRigFleet({ allowDelete: true })}

        {/* RECENT REPORTS / EXPENSES — rig services: on their own pages */}
        {!serviceCompany && (
        <>
        {reports.length > 0 && (
          <div style={{ marginBottom: "28px" }}>
            <h3
              style={{
                fontSize: "13px",
                letterSpacing: "3px",
                color: cyan,
                marginBottom: "16px",
                opacity: 0.8,
              }}
            >
              {t("dashboard.main.recentReports")}
            </h3>
            <p
              style={{
                color: "rgba(0,245,255,0.4)",
                fontSize: "11px",
                letterSpacing: "1px",
                marginBottom: "12px",
                marginTop: "-8px",
              }}
            >
              {t("dashboard.main.showing", { shown: Math.min(5, reports.length), total: reportsTotal })}
            </p>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {reports.slice(0, 5).map((r) => (
                <div
                  key={r._id}
                  style={{
                    backgroundColor: card,
                    border: `1px solid ${border}`,
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "6px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        color: cyan,
                        fontWeight: "700",
                        fontSize: "14px",
                      }}
                    >
                      {r.format === "tour_sheet" ? [r.rigName, r.customerName, r.wellName].filter(Boolean).join(" · ") : r.wellName}
                    </span>
                    <span
                      style={{
                        color: "rgba(255,255,255,0.3)",
                        fontSize: "11px",
                        letterSpacing: "1px",
                      }}
                    >
                      {formatDate(r.date)}
                    </span>
                  </div>
                  <p
                    style={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "13px",
                      marginBottom: "8px",
                      lineHeight: "1.5",
                    }}
                  >
                    {r.operations}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      fontSize: "11px",
                      color: "rgba(0,245,255,0.6)",
                      letterSpacing: "1px",
                    }}
                  >
                    {r.format === "tour_sheet" ? (
                      <>
                        <span>{t(`rigReport.status.${r.status}`)}</span>
                        {r.totals && <span>{t("rigReport.cardHours.working", { h: r.totals.working })}</span>}
                        {r.totals && r.totals.standby > 0 && <span>{t("rigReport.cardHours.standby", { h: r.totals.standby })}</span>}
                      </>
                    ) : (
                      <>
                        <span>📏 {r.footage} FT</span>
                        <span>⚖️ {r.mud_weight} PPG</span>
                        <span>🕐 {t("dashboard.main.hours", { hours: r.hours_drilling })}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RECENT EXPENSES */}
        {expenses.length > 0 && (
          <div style={{ marginBottom: "28px" }}>
            <h3
              style={{
                fontSize: "13px",
                letterSpacing: "3px",
                color: cyan,
                marginBottom: "16px",
                opacity: 0.8,
              }}
            >
              {t("dashboard.main.recentExpenses")}
            </h3>
            <p
              style={{
                color: "rgba(0,245,255,0.4)",
                fontSize: "11px",
                letterSpacing: "1px",
                marginBottom: "12px",
                marginTop: "-8px",
              }}
            >
              {t("dashboard.main.showing", { shown: Math.min(3, expenses.length), total: expensesTotal })}
            </p>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {expenses.slice(0, 3).map((e) => (
                <div
                  key={e._id}
                  style={{
                    backgroundColor: card,
                    border: `1px solid ${border}`,
                    borderRadius: "8px",
                    padding: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        alignItems: "center",
                        marginBottom: "4px",
                      }}
                    >
                      <span
                        style={{
                          color: cyan,
                          fontWeight: "700",
                          fontSize: "14px",
                        }}
                      >
                        {e.wellName}
                      </span>
                      <span
                        style={{
                          color: "rgba(255,255,255,0.3)",
                          fontSize: "11px",
                        }}
                      >
                        {formatDate(e.date)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        fontSize: "11px",
                        color: "rgba(0,245,255,0.6)",
                        letterSpacing: "1px",
                      }}
                    >
                      {e.rig_day_rate > 0 && (
                        <span>🏗 {t("dashboard.main.costRig", { amount: e.rig_day_rate.toLocaleString() })}</span>
                      )}
                      {e.crew_labor > 0 && (
                        <span>👷 {t("dashboard.main.costLabor", { amount: e.crew_labor.toLocaleString() })}</span>
                      )}
                      {e.fuel_diesel > 0 && (
                        <span>⛽ {t("dashboard.main.costFuel", { amount: e.fuel_diesel.toLocaleString() })}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div
                      style={{
                        color: cyan,
                        fontSize: "18px",
                        fontWeight: "800",
                      }}
                    >
                      ${e.total.toLocaleString()}
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.3)",
                        fontSize: "10px",
                        letterSpacing: "1px",
                      }}
                    >
                      {t("dashboard.main.total")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        </>
        )}

        {/* WELL STATUS GRID — not for rig-only companies (customer wells live under Jobs / Customers) */}
        {!rigOnly && (
        <>
        <h3
          style={{
            fontSize: "13px",
            letterSpacing: "3px",
            color: cyan,
            marginBottom: "16px",
            opacity: 0.8,
          }}
        >
          {t("dashboard.main.wellStatus")}
        </h3>

        {/* WELL SEARCH / FILTER / SORT */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <input
            value={wellSearchInput}
            onChange={(e) => setWellSearchInput(e.target.value)}
            placeholder={t("dashboard.main.searchWells")}
            style={{
              backgroundColor: "rgba(0,245,255,0.05)",
              color: "white",
              border: `1px solid ${border}`,
              borderRadius: "4px",
              padding: "6px 10px",
              fontSize: "11px",
              outline: "none",
              minWidth: "200px",
              flex: isMobile ? "1 1 100%" : "0 1 220px",
            }}
          />
          <input
            value={wellBasinInput}
            onChange={(e) => setWellBasinInput(e.target.value)}
            placeholder={t("dashboard.main.basin")}
            style={{
              backgroundColor: "rgba(0,245,255,0.05)",
              color: "white",
              border: `1px solid ${border}`,
              borderRadius: "4px",
              padding: "6px 10px",
              fontSize: "11px",
              outline: "none",
              width: "120px",
            }}
          />
          <input
            value={wellLocationInput}
            onChange={(e) => setWellLocationInput(e.target.value)}
            placeholder={t("dashboard.main.location")}
            style={{
              backgroundColor: "rgba(0,245,255,0.05)",
              color: "white",
              border: `1px solid ${border}`,
              borderRadius: "4px",
              padding: "6px 10px",
              fontSize: "11px",
              outline: "none",
              width: "140px",
            }}
          />
          <select
            value={wellPhaseFilter}
            onChange={(e) => setWellPhaseFilter(e.target.value)}
            style={{
              backgroundColor: "rgba(0,245,255,0.05)",
              color: "rgba(0,245,255,0.7)",
              border: `1px solid ${border}`,
              borderRadius: "4px",
              padding: "6px 10px",
              fontSize: "11px",
              outline: "none",
            }}
          >
            <option value="">{t("dashboard.main.allPhases")}</option>
            {["drilling", "completion", "workover", "production"].map((p) => (
              <option key={p} value={p}>{t(`wells.phases.${p}`)}</option>
            ))}
          </select>
          <select
            value={wellStatusFilter}
            onChange={(e) => setWellStatusFilter(e.target.value)}
            style={{
              backgroundColor: "rgba(0,245,255,0.05)",
              color: "rgba(0,245,255,0.7)",
              border: `1px solid ${border}`,
              borderRadius: "4px",
              padding: "6px 10px",
              fontSize: "11px",
              outline: "none",
            }}
          >
            <option value="">{t("dashboard.main.allStatuses")}</option>
            {["active", "standby", "completed", "shut-in"].map((s) => (
              <option key={s} value={s}>{t(`wells.status.${s}`)}</option>
            ))}
          </select>
          <div style={{ width: "1px", height: "20px", backgroundColor: border }} />
          <select
            value={wellSortBy}
            onChange={(e) => setWellSortBy(e.target.value)}
            style={{
              backgroundColor: "rgba(0,245,255,0.05)",
              color: "rgba(0,245,255,0.7)",
              border: `1px solid ${border}`,
              borderRadius: "4px",
              padding: "6px 10px",
              fontSize: "11px",
              outline: "none",
            }}
          >
            <option value="createdAt">{t("dashboard.main.sortNewest")}</option>
            <option value="name">{t("dashboard.main.sortName")}</option>
            <option value="location">{t("dashboard.main.sortLocation")}</option>
            <option value="phase">{t("dashboard.main.sortPhase")}</option>
          </select>
          <button
            onClick={() => setWellSortOrder(wellSortOrder === "asc" ? "desc" : "asc")}
            title={t("dashboard.main.toggleSort")}
            style={{
              background: "none",
              border: `1px solid ${border}`,
              color: "rgba(0,245,255,0.7)",
              fontSize: "11px",
              padding: "6px 10px",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {wellSortOrder === "asc" ? t("dashboard.main.asc") : t("dashboard.main.desc")}
          </button>
        </div>

        {/* GET-STARTED CARD — org genuinely has zero wells (not just a
            filtered search coming up empty, hence the no-filters-active
            check) and this role can act on it via Bulk Import. */}
        {wellsTotal === 0 &&
          !wellSearch &&
          !wellBasin &&
          !wellLocation &&
          !wellPhaseFilter &&
          !wellStatusFilter &&
          canBulkImportWells && (
            <div
              style={{
                textAlign: "center",
                padding: "48px 24px",
                border: `1px dashed ${border}`,
                borderRadius: "10px",
                backgroundColor: "rgba(0,245,255,0.02)",
                marginBottom: "16px",
              }}
            >
              <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", marginBottom: "20px" }}>
                {t("dashboard.emptyStates.noWellsYet")}
              </p>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  onClick={() => setShowBulkImport(true)}
                  style={{
                    background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: "#020817",
                    border: "none",
                    fontWeight: "700",
                    fontSize: "11px",
                    letterSpacing: "1px",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  {t("dashboard.emptyStates.bulkImportWellsCta")}
                </button>
                <button
                  onClick={() => setShowWellForm(true)}
                  style={{
                    background: "none",
                    border: `1px solid ${cyan}`,
                    color: cyan,
                    fontWeight: "700",
                    fontSize: "11px",
                    letterSpacing: "1px",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  {t("dashboard.emptyStates.addWellCta")}
                </button>
              </div>
            </div>
          )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fill, minmax(340px, 1fr))",
            gap: "16px",
          }}
        >
          {wells.map((well) => {
            const wellId = well.id || well._id;
            const wellWeather = weather[wellId];
            const wellWeatherError = weatherErrors[wellId];
            const wellJsa = jsaMap[wellId];
            const wellExpenses = expenses.filter((e) => e.wellId === wellId);
            const latest = wellExpenses[0] || null;
            const sevenDayTotal = wellExpenses.reduce(
              (sum, e) => sum + e.total,
              0,
            );
            const lightningRisk = wellWeather?.lightningRisk;
            const ringColor = lightningRisk
              ? STORM_RING_COLORS[lightningRisk.level]
              : statusColor[well.status];
            const highestRisk = wellJsa?.steps?.reduce((highest, s) => {
              const order = ["Low", "Medium", "High", "Critical"];
              return order.indexOf(s.riskLevel) > order.indexOf(highest)
                ? s.riskLevel
                : highest;
            }, "");

            return (
              <div
                key={wellId}
                onClick={() => setSelectedWell(well)}
                style={{
                  backgroundColor: card,
                  borderRadius: "10px",
                  padding: "20px",
                  border: `1px solid ${lightningRisk ? ringColor + "88" : border}`,
                  boxShadow: lightningRisk
                    ? `0 0 20px ${ringColor}33`
                    : "0 0 20px rgba(0,245,255,0.03)",
                  cursor: "pointer",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = "rgba(0,245,255,0.4)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = lightningRisk
                    ? ringColor + "88"
                    : border)
                }
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "14px",
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontWeight: "700",
                        fontSize: "15px",
                        color: "white",
                        marginBottom: "2px",
                      }}
                    >
                      {well.well_name}
                    </h3>
                    <p
                      style={{
                        color: "rgba(0,245,255,0.4)",
                        fontSize: "11px",
                        letterSpacing: "2px",
                      }}
                    >
                      {well.basin?.toUpperCase()}
                    </p>
                  </div>
                  <span
                    style={{
                      backgroundColor: statusColor[well.status] + "22",
                      color: statusColor[well.status],
                      fontSize: "10px",
                      fontWeight: "700",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      border: `1px solid ${statusColor[well.status]}`,
                      letterSpacing: "1px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ● {t(`wells.status.${well.status}`, { defaultValue: well.status?.toUpperCase() })}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  {[
                    { label: t("dashboard.main.locationLabel"), value: well.location },
                    {
                      label: t("dashboard.main.phaseLabel"),
                      value: wellPhaseLabel(t, well),
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      style={{
                        backgroundColor: "rgba(0,245,255,0.05)",
                        borderRadius: "6px",
                        padding: "8px 10px",
                      }}
                    >
                      <div
                        style={{
                          color: "rgba(0,245,255,0.4)",
                          fontSize: "9px",
                          letterSpacing: "2px",
                          marginBottom: "2px",
                        }}
                      >
                        {s.label}
                      </div>
                      <div style={{ color: "white", fontSize: "12px" }}>
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>

                {wellJsa ? (
                  <div
                    style={{
                      backgroundColor: "rgba(0,245,255,0.04)",
                      border: `1px solid ${border}`,
                      borderRadius: "8px",
                      padding: "10px 12px",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <div
                        style={{
                          color: cyan,
                          fontSize: "9px",
                          fontWeight: "700",
                          letterSpacing: "2px",
                        }}
                      >
                        {t("dashboard.main.todaysJsa")}
                      </div>
                      {wellJsa.locked && (
                        <span
                          style={{
                            color: "#ffaa00",
                            fontSize: "9px",
                            letterSpacing: "1px",
                          }}
                        >
                          {t("dashboard.main.locked")}
                        </span>
                      )}
                    </div>
                    {/* JSA stats — 2 cols on mobile, 4 on desktop */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr 1fr"
                          : "repeat(4, 1fr)",
                        gap: "6px",
                      }}
                    >
                      {[
                        { label: t("dashboard.main.jsaJobType"), value: wellJsa.jobType ? jobTypeLabel(t, wellJsa.jobType) : t("dashboard.main.na") },
                        { label: t("dashboard.main.jsaSteps"), value: wellJsa.steps?.length || 0 },
                        {
                          label: t("dashboard.main.jsaSigs"),
                          value:
                            wellJsa.signatures?.filter((s) => s.name).length ||
                            0,
                        },
                        {
                          label: t("dashboard.main.jsaMaxRisk"),
                          value: highestRisk
                            ? t(`dashboard.main.risk.${highestRisk}`, { defaultValue: highestRisk })
                            : t("dashboard.main.na"),
                          color: highestRisk
                            ? RISK_COLORS[highestRisk]
                            : "rgba(255,255,255,0.4)",
                        },
                      ].map((stat) => (
                        <div
                          key={stat.label}
                          style={{
                            backgroundColor: "rgba(0,245,255,0.05)",
                            borderRadius: "4px",
                            padding: "6px",
                          }}
                        >
                          <div
                            style={{
                              color: "rgba(0,245,255,0.4)",
                              fontSize: "7px",
                              letterSpacing: "1px",
                              marginBottom: "2px",
                            }}
                          >
                            {stat.label}
                          </div>
                          <div
                            style={{
                              color: stat.color || "white",
                              fontSize: "11px",
                              fontWeight: "700",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {stat.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : showNoJsaWarning({ wellId, serviceCompany, workingWellIds }) ? (
                  <div
                    style={{
                      backgroundColor: "rgba(255,68,102,0.05)",
                      border: "1px solid rgba(255,68,102,0.2)",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>⚠️</span>
                    <span
                      style={{
                        color: "rgba(255,68,102,0.8)",
                        fontSize: "10px",
                        letterSpacing: "2px",
                        fontWeight: "700",
                      }}
                    >
                      {t("dashboard.main.noJsaToday")}
                    </span>
                  </div>
                ) : null}

                {latest && (
                  <div
                    style={{
                      borderTop: `1px solid ${border}`,
                      paddingTop: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: "rgba(0,245,255,0.4)",
                            fontSize: "9px",
                            letterSpacing: "2px",
                            marginBottom: "2px",
                          }}
                        >
                          {t("dashboard.main.latestCost")}
                        </div>
                        <div
                          style={{
                            color: cyan,
                            fontSize: "14px",
                            fontWeight: "700",
                          }}
                        >
                          ${latest.total.toLocaleString()}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            color: "rgba(0,245,255,0.4)",
                            fontSize: "9px",
                            letterSpacing: "2px",
                            marginBottom: "2px",
                          }}
                        >
                          {t("dashboard.main.sevenDayTotal")}
                        </div>
                        <div
                          style={{
                            color: "white",
                            fontSize: "14px",
                            fontWeight: "700",
                          }}
                        >
                          ${sevenDayTotal.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {wellWeather && (
                  <div
                    style={{
                      borderTop: `1px solid ${border}`,
                      paddingTop: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: cyan,
                            fontSize: "20px",
                            fontWeight: "700",
                          }}
                        >
                          {Math.round(wellWeather.temperature)}°F
                        </div>
                        <div
                          style={{
                            color: "rgba(255,255,255,0.4)",
                            fontSize: "11px",
                          }}
                        >
                          {wellWeather.conditions}
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "right",
                          fontSize: "11px",
                          color: "rgba(0,245,255,0.5)",
                        }}
                      >
                        <div>
                          💨 {t("dashboard.main.wind", { speed: Math.round(wellWeather.windSpeed) })}{" "}
                          {wellWeather.windGust
                            ? t("dashboard.main.gusts", { gust: Math.round(wellWeather.windGust) })
                            : ""}
                        </div>
                        <div>💧 {wellWeather.humidity}%</div>
                        {wellWeather.snow1h && (
                          <div>❄️ {t("dashboard.main.snowPerHour", { inches: wellWeather.snow1h })}</div>
                        )}
                        {wellWeather.rain1h && (
                          <div>🌧 {t("dashboard.main.rainPerHour", { inches: wellWeather.rain1h })}</div>
                        )}
                      </div>
                    </div>
                    <LightningBadge risk={lightningRisk} />
                  </div>
                )}

                {!wellWeather && wellWeatherError && (
                  <div
                    style={{
                      borderTop: `1px solid ${border}`,
                      paddingTop: "12px",
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        color: "rgba(255,255,255,0.3)",
                        fontSize: "11px",
                        letterSpacing: "1px",
                      }}
                    >
                      {t("dashboard.main.weatherUnavailable")}
                    </span>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "8px",
                  }}
                >
                  <span
                    style={{
                      color: "rgba(0,245,255,0.3)",
                      fontSize: "10px",
                      letterSpacing: "2px",
                    }}
                  >
                    {isMobile ? t("dashboard.main.tapToView") : t("dashboard.main.clickToView")}
                  </span>
                  {!serviceCompany && user.role === "admin" && (
                    <button
                      onClick={(e) => deleteWell(wellId, e)}
                      style={{
                        background: "none",
                        border: "1px solid rgba(255,68,102,0.4)",
                        color: "rgba(255,68,102,0.7)",
                        fontSize: "10px",
                        letterSpacing: "1px",
                        padding: "3px 10px",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      {t("dashboard.main.delete")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <Pagination
          page={wellsPage}
          limit={25}
          total={wellsTotal}
          totalPages={wellsTotalPages}
          onPageChange={handleWellsPageChange}
          itemLabel={t("dashboard.main.wellsItemLabel")}
        />
        </>
        )}
      </main>

      {showChat && (
        <ChatPanel
          token={token}
          user={user}
          wells={wells}
          onClose={() => setShowChat(false)}
        />
      )}
    </div>
  );
}

export default Dashboard;

